import Phaser from 'phaser'
import ObstaclesController from './ObstaclesController'
import PlayerController, { type PlayerBudgetState } from './PlayerController'
import TpvVoladorController from './TpvVoladorController'
import CajeroController from './CajeroController'
import {
	setLogoOverlayVisible,
	sharedInstance as events,
	setPersistentBudgetState,
	getPersistentBudgetState,
	clearPersistentBudgetState,
	setPersistentPlayTimeState,
	getPersistentPlayTimeState,
	clearPersistentPlayTimeState,
	setPersistentRankingRunSession,
	getPersistentRankingRunSession,
	clearPersistentRankingRunSession,
	setPersistentGameOverSummary,
	clearPersistentGameOverSummary
} from './EventCenter'
import RankingApiClient from '../services/RankingApiClient'
import {
	isLocalSessionId,
	createLocalSessionId,
	enqueueStartOperation,
	enqueueEventOperation,
	enqueueFinishOperation,
	checkConnectivityAndApiAvailability,
	flushQueuedOperations
} from '../services/OfflineRankingQueue'
import {
	createInactivityModal as createSharedInactivityModal,
	INACTIVITY_MODAL_TEXTURE_KEY,
	INACTIVITY_MODAL_TEXTURE_PATH,
	type InactivityModal
} from './InactivityModal'
import { getLevelTransitionVideoCacheKey } from './LevelTransitionVideo'

type LevelConfig = {
	mapKey: string
	mapJsonPath: string
	tilesetImageKey: string
	tilesetImagePath: string
	tilesetName: string
    videoPath: string
	groundCollisionTileFactor?: number
	finalLockTiles?: number
    timeLimit?: number
}

const LEVELS: LevelConfig[] = [
	{
		mapKey: 'level-1-map',
		mapJsonPath: 'assets/londres.json',
		tilesetImageKey: 'level-1-tiles',
		tilesetImagePath: 'assets/sprites-londres.png',
		tilesetName: 'sprites-londres',
        videoPath: 'assets/bali/video-transicion-1.mp4',
		groundCollisionTileFactor: 0.65,
        timeLimit: 180
	},
	{
		mapKey: 'level-2-map',
		mapJsonPath: 'assets/bali.json',
		tilesetImageKey: 'level-2-tiles',
		tilesetImagePath: 'assets/sprites-bali.png',
		tilesetName: 'bali',
        videoPath: 'assets/tokio/video-transicion-2.mp4',
		groundCollisionTileFactor: 0.15,
        timeLimit: 240
    },
	{
		mapKey: 'level-3-map',
		mapJsonPath: 'assets/tokio.json',
		tilesetImageKey: 'level-3-tiles',
		tilesetImagePath: 'assets/sprites-tokio.png',
		tilesetName: 'tokio',
        videoPath: 'assets/final.mp4',
		groundCollisionTileFactor: 0.95,
		finalLockTiles: 25,
        timeLimit: 120
	}
]

const START_LEVEL_INDEX = 0
const START_PLAYER_Y = 20
const DEFAULT_GROUND_COLLISION_TILE_FACTOR = 0.65
const GROUND_COLLISION_OFFSET_ROWS_FROM_BOTTOM = 3
const LONDON_BACKGROUND_KEY = 'bg-londres'
const LONDON_BACKGROUND_PATH = 'assets/bg_londres.jpg'
const LONDON_MAP_KEY = 'level-1-map'
const BALI_BACKGROUND_KEY = 'bg-bali'
const BALI_BACKGROUND_PATH = 'assets/bali/bg_bali.jpg'
const BALI_MAP_KEY = 'level-2-map'
const TOKIO_BACKGROUND_KEY = 'bg-tokio'
const TOKIO_BACKGROUND_PATH = 'assets/tokio/bg_tokio.jpg'
const TOKIO_MAP_KEY = 'level-3-map'
const LONDON_PARALLAX_SCROLL_FACTOR_X = 0.2
const LONDON_PARALLAX_SCROLL_FACTOR_Y = 0.04
const CLOUD_PARALLAX_SPEED_MULTIPLIER_X = 3.5
const CLOUD_PARALLAX_SPEED_MULTIPLIER_Y = 1.5
const CLOUD_PARALLAX_MAX_FACTOR = 0.95
const BUILDING_PARALLAX_MIN_FACTOR_X = 1
const COIN_SPRITESHEET_KEY = 'coin'
const COIN_SPRITESHEET_PATH = 'assets/moneda.png'
const COIN_FRAME_WIDTH = 64
const COIN_FRAME_HEIGHT = 64
const COIN_TYPE_BIZUM = 'bizum'
const COIN_BUDGET_BY_TYPE: Record<string, number> = {
	[COIN_TYPE_BIZUM]: 50,
	nomina: 250,
	cashback: 300,
	devolucion_comision: 800
}
const DEFAULT_COIN_BUDGET_POINTS = COIN_BUDGET_BY_TYPE[COIN_TYPE_BIZUM]
const CARD_POWERUP_OBJECT_TYPE = 'card-powerup'
const CARD_POWERUP_TEXTURE_KEY = 'card-powerup'
const CARD_POWERUP_TEXTURE_PATH = 'assets/tarjeta_potenciador.png'
const CARD_POWERUP_DEFAULT_WIDTH = 81
const CARD_POWERUP_DEFAULT_HEIGHT = 50
const CARD_POWERUP_FLOAT_OFFSET_PX = 20
const CARD_POWERUP_FLOAT_DURATION_MS = 1000
const BOOMERANG_CARD_TEXTURE_KEY = 'boomerang-card'
const BOOMERANG_CARD_TEXTURE_PATH = 'assets/tarjeta.png'
const INACTIVITY_WARNING_DELAY_MS = 15000
const INACTIVITY_RESTART_DELAY_MS = 10000
const INACTIVITY_MODAL_DEPTH = 10000
const CLOUD_TEXTURES = {
	nube1: { key: 'cloud-1', image: 'nube1.png' },
	nube2: { key: 'cloud-2', image: 'nube2.png' },
	nube3: { key: 'cloud-3', image: 'nube3.png' },
	nube4: { key: 'cloud-4', image: 'nube3.png' },
	nube5: { key: 'cloud-5', image: 'nube5.png' }
} as const
const CLOUD_RENDER_DEPTH = -500
const BUILDING_TEXTURES = {
	BigBen: 'building-big-ben',
	'london-bridge': 'building-london-bridge',
	bpalace: 'building-bpalace'
} as const
const BUILDING_BIG_BEN_IMAGE_PATH = 'assets/big-ben.png'
const BUILDING_LONDON_BRIDGE_IMAGE_PATH = 'assets/london-bridge.png'
const BUILDING_BPALACE_IMAGE_PATH = 'assets/bpalace.png'
const BUILDING_RENDER_DEPTH = -350
const STREET_FURNITURE_RENDER_DEPTH = 120
const GAMEPLAY_RENDER_DEPTH = 200
const BLOCK_RENDER_DEPTH = 180
const BLOCKING_STREET_FURNITURE_NAMES = ['bus', 'nube-suelo', 'bloque'] as const
const DEFAULT_OBJECT_IMAGES: Record<string, string> = {
	'nube-suelo': 'nube-suelo.png',
	bloque: 'bloque.png'
}
const BLOCKING_STREET_FURNITURE_CHAMFER_RATIO = 30
const BLOCKING_STREET_FURNITURE_CHAMFER_MAX_PX = 50
const BLOCKING_PLATFORM_BOTTOM_CHAMFER_RATIO = 0.95
const BLOCKING_PLATFORM_BOTTOM_CHAMFER_MAX_PX = 250
const NUBE_SUELO_COLLIDER_TOP_TRIM_RATIO = 0.2
const NUBE_SUELO_CHAMFER_RATIO = 0.08
const NUBE_SUELO_CHAMFER_MAX_PX = 18
const GROUND_COLLISION_CATEGORY = 0x0002
const ENEMY_DECORATIVE_NO_COLLISION_MASK = 0x0000
const TPV_VOLADOR_SPRITESHEET_KEY = 'tpv-volador'
const TPV_VOLADOR_SPRITESHEET_PATH = 'assets/enemigo_tpv_volador.png'
const ESCANER_SPRITESHEET_KEY = 'enemigo-escaner'
const ESCANER_SPRITESHEET_PATH = 'assets/enemigo_escaner.png'
const TPV_VOLADOR_FRAME_WIDTH = 128
const TPV_VOLADOR_FRAME_HEIGHT = 128
const CAJERO_VERDE_SPRITESHEET_KEY = 'cajero-verde'
const CAJERO_VERDE_SPRITESHEET_PATH = 'assets/enemigo_cajero_verde.png'
const CAJERO_AMARILLO_SPRITESHEET_KEY = 'cajero-amarillo'
const CAJERO_AMARILLO_SPRITESHEET_PATH = 'assets/enemigo_cajero_amarillo.png'
const CAJERO_FRAME_WIDTH = 128
const CAJERO_FRAME_HEIGHT = 128
const CAJERO_VERDE_DEFAULT_MOVE_SPEED = 1.1
const CAJERO_AMARILLO_DEFAULT_MOVE_SPEED = 2.5
const TPV_VOLADOR_ENEMY_HIT_BUDGET_LOSS = 1200
const CAJERO_VERDE_ENEMY_HIT_BUDGET_LOSS = 500
const CAJERO_AMARILLO_ENEMY_HIT_BUDGET_LOSS = 800
const CAJERO_NO_SELF_COLLISION_GROUP = -2
const TPV_VOLADOR_DEFAULT_TRAVEL_DISTANCE_MIN = 150
const TPV_VOLADOR_DEFAULT_TRAVEL_DISTANCE_MAX = 280
const ESCANER_DEFAULT_TRAVEL_DISTANCE_MIN = 260
const ESCANER_DEFAULT_TRAVEL_DISTANCE_MAX = 420
const ESCANER_DEFAULT_MOVE_SPEED = 6.2
const CAJERO_DEFAULT_TRAVEL_DISTANCE_MIN = 150
const CAJERO_DEFAULT_TRAVEL_DISTANCE_MAX = 250
const TPV_VOLADOR_COLLIDER_DEFAULT_DECORATIVE = {
	widthRatio: 1,
	heightRatio: 1,
	bottomOffset: 0
}
const CAJERO_COLLIDER_DEFAULT_DECORATIVE = {
	widthRatio: 1,
	heightRatio: 1,
	bottomOffset: 0
}
const TPV_VOLADOR_COLLIDER_DEFAULT_NORMAL = {
	widthRatio: 0.62,
	heightRatio: 0.87,
	bottomOffset: 0
}
const CAJERO_COLLIDER_DEFAULT_NORMAL = {
	widthRatio: 0.60,
	heightRatio: 0.9,
	bottomOffset: 0
}
const LEVEL_ONE_INDEX = 0
const LEVEL_THREE_INDEX = 2
const LEVEL_END_EXIT_TRAVEL_PX = 260
const LEVEL_END_EXIT_DURATION_MS = 1200
const LEVEL_END_GROUND_ALIGN_DURATION_MS = 260
const LEVEL_END_GROUND_ALIGN_TOLERANCE_PX = 2
const LEVEL_END_STABLE_VERTICAL_SPEED_MAX = 0.25
const LEVEL_END_GROUND_SCAN_STEP_PX = 28
const LEVEL_END_GROUND_SCAN_MAX_LOOKAHEAD_PX = 220
const LEVEL_END_FINISH_GROUND_TOLERANCE_PX = 16
const LEVEL_THREE_CAMERA_END_TOLERANCE_PX = 1
const LEVEL_THREE_FINAL_CAMERA_LOCK_TO_GROUND_TWEEN_MS = 280
const LEVEL_TRANSITION_VIDEO_SCENE_KEY = 'level-transition-video'
const DAILY_RANKING_SCENE_KEY = 'daily-ranking'
const LEVEL_BACKGROUND_MUSIC_VOLUME = 0.15
const LEVEL_BACKGROUND_MUSIC_RATE_NORMAL = 1
const LEVEL_BACKGROUND_MUSIC_RATE_CRITICAL = 1.2
const LEVEL_TIME_CRITICAL_THRESHOLD_SECONDS = 30
const ACTION_SFX_VOLUME = 0.45
const LEVEL_BACKGROUND_MUSIC_KEYS = ['music-level-1', 'music-level-2', 'music-level-3'] as const
const LEVEL_BACKGROUND_MUSIC_PATHS = ['assets/musica/nivel-1.mp3', 'assets/musica/nivel-2.mp3', 'assets/musica/nivel-3.mp3'] as const
const FINAL_BOSS_MUSIC_KEY = 'music-final-boss'
const FINAL_BOSS_MUSIC_PATH = 'assets/musica/enemigo-final.mp3'
const SFX_ENEMY_DESTROYED_KEY = 'sfx-enemy-destroyed'
const SFX_ENEMY_DESTROYED_PATH = 'assets/sonidos/destruccion_enemigo.mp3'
const SFX_PLAYER_HIT_KEY = 'sfx-player-hit'
const SFX_PLAYER_HIT_PATH = 'assets/sonidos/golpe_recibido.mp3'
const SFX_COIN_COLLECTED_KEY = 'sfx-coin-collected'
const SFX_COIN_COLLECTED_PATH = 'assets/sonidos/recoger_moneda.mp3'
const SFX_CARD_POWERUP_COLLECTED_KEY = 'sfx-card-powerup-collected'
const SFX_CARD_POWERUP_COLLECTED_PATH = 'assets/sonidos/mejora_tarjeta.mp3'
const SFX_JUMP_KEY = 'sfx-jump'
const SFX_JUMP_PATH = 'assets/sonidos/salto.mp3'
const FINAL_BOSS_SPRITESHEET_KEY = 'final-boss'
const FINAL_BOSS_SPRITESHEET_PATH = 'assets/enemigo_final.png'
const FINAL_BOSS_FRAME_WIDTH = 400
const FINAL_BOSS_FRAME_HEIGHT = 400
const FINAL_BOSS_RENDER_DEPTH = GAMEPLAY_RENDER_DEPTH
const FINAL_BOSS_MAX_HEALTH_PERCENT = 100
const FINAL_BOSS_CARD_HIT_DAMAGE_PERCENT = 7
const FINAL_BOSS_MOVE_SPEED_MIN_PX_PER_MS = 0.05
const FINAL_BOSS_MOVE_SPEED_MAX_PX_PER_MS = 2.2
const FINAL_BOSS_ENTRY_MOVE_SPEED_PX_PER_MS = 1.2
const FINAL_BOSS_ENTRY_OFFSET_X = 220
const FINAL_BOSS_ENTRY_OFFSET_Y = 260
const FINAL_BOSS_ENTRY_TARGET_TILE_X = 95
const FINAL_BOSS_PATROL_PADDING_X = 44
const FINAL_BOSS_MIN_PATROL_SPAN_PX = 320
const FINAL_BOSS_ENTRY_PAUSE_DURATION_MS = 0
const FINAL_BOSS_DAMAGE_IMMUNITY_MS = 1000
const FINAL_BOSS_DAMAGE_IMMUNITY_BLINK_DURATION_MS = 90
const FINAL_BOSS_FRAME_COLUMNS = 8
const FINAL_BOSS_ENTRY_IDLE_FRAME_ROW = 1
const FINAL_BOSS_ENTRY_IDLE_FRAME_COLUMN = 5
const FINAL_BOSS_ENTRY_IDLE_FRAME_INDEX =
	((FINAL_BOSS_ENTRY_IDLE_FRAME_ROW - 1) * FINAL_BOSS_FRAME_COLUMNS)
	+ (FINAL_BOSS_ENTRY_IDLE_FRAME_COLUMN - 1)
const FINAL_BOSS_CONTACT_BUDGET_LOSS = 500
const FINAL_BOSS_MIN_TRAVEL_TILES = 6
const FINAL_BOSS_COLLISION_MARGIN_TILES = 2
const FINAL_BOSS_TRAVEL_TILE_SIZE_PX = 78
const FINAL_BOSS_SEGMENT_DURATION_MIN_MS = 2000
const FINAL_BOSS_SEGMENT_DURATION_MAX_MS = 3000
const FINAL_BOSS_COLLISION_DIRECTION_CHANGE_COOLDOWN_MS = 170
const FINAL_BOSS_COLLISION_HORIZONTAL_ALIGNMENT_MIN_PX = 18
const FINAL_BOSS_COLLISION_REVERSE_MIN_PROGRESS_PX = 8
const FINAL_BOSS_DEBUG_LOG_ENABLED = true
const FINAL_BOSS_DEBUG_LOG_INTERVAL_MS = 250
const FINAL_BOSS_JUMP_HEIGHT_TILES = 7
const FINAL_BOSS_JUMP_DURATION_MS = 900

type EnemyColliderConfig = {
	widthRatio: number
	heightRatio: number
	bottomOffset: number
}

const FINAL_BOSS_COLLIDER: EnemyColliderConfig = {
	widthRatio: 0.56,
	heightRatio: 0.72,
	bottomOffset: 14
}

const FINAL_BOSS_ANIM_WALK_LEFT = 'final-boss-walk-left'
const FINAL_BOSS_ANIM_WALK_RIGHT = 'final-boss-walk-right'
const FINAL_BOSS_ANIM_HIT_LEFT = 'final-boss-hit-left'
const FINAL_BOSS_ANIM_HIT_RIGHT = 'final-boss-hit-right'

type MovingCloud = {
	sprite: Phaser.GameObjects.Image
}

type MovingBuilding = {
	sprite: Phaser.GameObjects.Image
}

type StaticBuildingSpec = {
	x: number
	y: number
	width: number
	height: number
	textureKey: string
	texturePath?: string
	renderDepth: number
	isBlocking: boolean
	blockingTopTrimRatio: number
	blockingChamferRatio: number
	blockingChamferMaxPx: number
	blockingBottomOnlyChamfer: boolean
	isParallaxDriftCandidate: boolean
}

type MovingCloudSpec = {
	x: number
	y: number
	width: number
	height: number
	textureKey: string
	texturePath?: string
}

type CoinRewardSpec = {
	coinType: string
	budgetPoints: number
}

type CoinCollectedPayload = {
	amount: number
	rewardType: string
}

type EnemyHitPayload = {
	amount: number
	enemyType: string
}

type FinalBossCardHitPayload = {
	targetBodyId?: number
}

type SpikeHitPayload = {
	amount: number
}

type HealthPickedPayload = {
	amount: number
}

type LevelTimeChangedPayload = {
	remainingMs: number
	visible: boolean
}

const DEFAULT_NICKNAME = 'SLAYER'

export default class Game extends Phaser.Scene
{
	private cursors!: Phaser.Types.Input.Keyboard.CursorKeys

	private penquin?: Phaser.Physics.Matter.Sprite
	private playerController?: PlayerController
	private obstacles!: ObstaclesController
	private tpvVoladores: TpvVoladorController[] = []
	private cajeros: CajeroController[] = []
	private londonBackground?: Phaser.GameObjects.TileSprite
	private londonResizeHandler?: (gameSize: Phaser.Structs.Size) => void
	private londonParallaxFactorX = LONDON_PARALLAX_SCROLL_FACTOR_X
	private londonParallaxFactorY = LONDON_PARALLAX_SCROLL_FACTOR_Y
	private cloudParallaxFactorX = Math.min(CLOUD_PARALLAX_MAX_FACTOR, LONDON_PARALLAX_SCROLL_FACTOR_X * CLOUD_PARALLAX_SPEED_MULTIPLIER_X)
	private cloudParallaxFactorY = Math.min(CLOUD_PARALLAX_MAX_FACTOR, LONDON_PARALLAX_SCROLL_FACTOR_Y * CLOUD_PARALLAX_SPEED_MULTIPLIER_Y)
	private buildingParallaxFactorX = BUILDING_PARALLAX_MIN_FACTOR_X
	private currentLevelIndex = START_LEVEL_INDEX
	private currentLevel: LevelConfig = LEVELS[START_LEVEL_INDEX]
	private movingClouds: MovingCloud[] = []
	private movingBuildings: MovingBuilding[] = []
	private staticBuildings: Phaser.GameObjects.Image[] = []
	private enemiesDecorativeMode = false
	private tpvVoladorColliderDefaults: EnemyColliderConfig = TPV_VOLADOR_COLLIDER_DEFAULT_NORMAL
	private cajeroColliderDefaults: EnemyColliderConfig = CAJERO_COLLIDER_DEFAULT_NORMAL
	private inactivityModal?: InactivityModal
	private inactivityWarningEvent?: Phaser.Time.TimerEvent
	private inactivityRestartEvent?: Phaser.Time.TimerEvent
	private inactivityCountdownEvent?: Phaser.Time.TimerEvent
	private inactivityLastActivityAtMs = 0
	private inactivityEnabled = false
	private currentMapWidthPx = 0
	private currentMapHeightPx = 0
	private currentMapTileWidthPx = 0
	private currentMapTileHeightPx = 0
	private levelExitSequenceActive = false
	private levelExitTween?: Phaser.Tweens.Tween
	private levelMusic?: Phaser.Sound.BaseSound
	private finalBossMusicTriggered = false
	private levelThreeFinalLockActive = false
	private levelThreeFinalCameraLocked = false
	private levelThreeFinalCameraScrollX = 0
	private levelThreeFinalCameraScrollY = 0
	private levelThreeFinalCameraLockTween?: Phaser.Tweens.Tween
	private finalBoss?: Phaser.Physics.Matter.Sprite
	private finalBossHealthPercent = FINAL_BOSS_MAX_HEALTH_PERCENT
	private finalBossDirection: -1 | 1 = -1
	private finalBossMoveDirection: -1 | 1 = -1
	private finalBossPatrolMinX = 0
	private finalBossPatrolMaxX = 0
	private finalBossEntryStartX = 0
	private finalBossEntryStartY = 0
	private finalBossEntryTargetX = 0
	private finalBossEntryTargetY = 0
	private finalBossEntering = false
	private finalBossEntryPauseUntilMs = 0
	private finalBossTargetX = 0
	private finalBossCurrentSpeedPxPerMs = FINAL_BOSS_MOVE_SPEED_MIN_PX_PER_MS
	private finalBossHitRecoverUntilMs = 0
	private finalBossDamageImmunityTween?: Phaser.Tweens.Tween
	private finalBossDamageImmunitySequence = 0
	private finalBossDirectionChangeLockedUntilMs = 0
	private finalBossEntryTween?: Phaser.Tweens.Tween
	private finalBossRetreatTargetX = 0
	private finalBossRetreating = false
	private finalBossDefeated = false
	private finalBossLockedY = 0
	private finalBossJumpOffsetPx = 0
	private finalBossJumpTween?: Phaser.Tweens.Tween
	private finalBossSegmentStartX = 0
	private finalBossPreviousX = 0
	private finalBossDebugLastLogAtMs = -Infinity
	private lastStableGroundY?: number
	private pendingLevelAfterVideoIndex?: number
	private pendingLevelStartBudgetState?: PlayerBudgetState
	private elapsedMapTimeMs = 0
	private elapsedMapTimeLastSecond = -1
	private levelTimeRemainingMs = 0
	private levelTimeLastSecond = -1
	private levelTimeActive = false
	private levelTimeVisible = false
	private levelTimeExpired = false
	private levelCriticalMusicActive = false
	private levelEndTriggerX?: number
	private rankingApi?: RankingApiClient
	private pendingRunStartPromise?: Promise<void>

	constructor()
	{
		super('game')
	}

	init(data: {
		levelIndex?: number
		enemiesDecorative?: boolean
		tpvCollider?: Partial<EnemyColliderConfig>
		cajeroCollider?: Partial<EnemyColliderConfig>
		budgetState?: PlayerBudgetState
	} = {})
	{
		if (LEVELS.length === 0)
		{
			throw new Error('No hay niveles configurados en LEVELS')
		}

		const requestedLevelIndex = data.levelIndex ?? START_LEVEL_INDEX
		this.currentLevelIndex = Phaser.Math.Clamp(requestedLevelIndex, 0, LEVELS.length - 1)
		this.currentLevel = LEVELS[this.currentLevelIndex]

		if (this.currentLevelIndex === START_LEVEL_INDEX && !data.budgetState)
		{
			clearPersistentBudgetState()
			clearPersistentPlayTimeState()
			clearPersistentRankingRunSession()
			clearPersistentGameOverSummary()
		}

		this.cursors = this.input.keyboard.createCursorKeys()
		this.obstacles = new ObstaclesController()
		this.tpvVoladores = []
		this.cajeros = []
		this.movingClouds = []
		this.movingBuildings = []
		this.staticBuildings = []
		this.enemiesDecorativeMode = Boolean(data.enemiesDecorative)
		this.currentMapWidthPx = 0
		this.currentMapHeightPx = 0
		this.currentMapTileWidthPx = 0
		this.currentMapTileHeightPx = 0
		this.levelExitSequenceActive = false
		this.levelExitTween = undefined
		this.finalBossMusicTriggered = false
		this.levelThreeFinalLockActive = false
		this.levelThreeFinalCameraLocked = false
		this.levelThreeFinalCameraScrollX = 0
		this.levelThreeFinalCameraScrollY = 0
		if (this.levelThreeFinalCameraLockTween)
		{
			this.levelThreeFinalCameraLockTween.stop()
			this.levelThreeFinalCameraLockTween = undefined
		}
		this.finalBoss?.destroy()
		this.finalBoss = undefined
		this.finalBossHealthPercent = FINAL_BOSS_MAX_HEALTH_PERCENT
		this.finalBossDirection = -1
		this.finalBossMoveDirection = -1
		this.finalBossPatrolMinX = 0
		this.finalBossPatrolMaxX = 0
		this.finalBossEntryStartX = 0
		this.finalBossEntryStartY = 0
		this.finalBossEntryTargetX = 0
		this.finalBossEntryTargetY = 0
		this.finalBossEntering = false
		this.finalBossEntryPauseUntilMs = 0
		this.finalBossTargetX = 0
		this.finalBossCurrentSpeedPxPerMs = FINAL_BOSS_MOVE_SPEED_MIN_PX_PER_MS
		this.finalBossHitRecoverUntilMs = 0
		if (this.finalBossDamageImmunityTween)
		{
			this.finalBossDamageImmunityTween.stop()
			this.finalBossDamageImmunityTween = undefined
		}
		this.finalBossDamageImmunitySequence = 0
		this.finalBossDirectionChangeLockedUntilMs = 0
		this.finalBossEntryTween = undefined
		this.finalBossRetreatTargetX = 0
		this.finalBossRetreating = false
		this.finalBossDefeated = false
		this.finalBossLockedY = 0
		this.finalBossJumpOffsetPx = 0
		if (this.finalBossJumpTween)
		{
			this.finalBossJumpTween.stop()
			this.finalBossJumpTween = undefined
		}
		this.finalBossSegmentStartX = 0
		this.finalBossPreviousX = 0
		this.finalBossDebugLastLogAtMs = -Infinity
		this.lastStableGroundY = undefined
		this.pendingLevelAfterVideoIndex = undefined
		this.pendingLevelStartBudgetState = data.budgetState ?? getPersistentBudgetState()
		this.elapsedMapTimeMs = getPersistentPlayTimeState()?.elapsedMs ?? 0
		this.elapsedMapTimeLastSecond = Math.floor(this.elapsedMapTimeMs / 1000)
		const configuredLevelTimeLimitSeconds = this.currentLevel.timeLimit
		if (typeof configuredLevelTimeLimitSeconds === 'number' && Number.isFinite(configuredLevelTimeLimitSeconds) && configuredLevelTimeLimitSeconds > 0)
		{
			this.levelTimeRemainingMs = Math.floor(configuredLevelTimeLimitSeconds * 1000)
			this.levelTimeLastSecond = Math.floor(this.levelTimeRemainingMs / 1000)
			this.levelTimeActive = true
			this.levelTimeVisible = true
		}
		else
		{
			this.levelTimeRemainingMs = 0
			this.levelTimeLastSecond = -1
			this.levelTimeActive = false
			this.levelTimeVisible = false
		}
		this.levelTimeExpired = false
		this.levelCriticalMusicActive = false
		this.levelEndTriggerX = undefined
		const rankingApiBaseUrl = this.game.registry.get('rankingApiBaseUrl')
		this.rankingApi = new RankingApiClient(typeof rankingApiBaseUrl === 'string' ? rankingApiBaseUrl : '/ranking/api/v1')
		void this.syncOfflineRankingQueue()
		this.tpvVoladorColliderDefaults = {
			...TPV_VOLADOR_COLLIDER_DEFAULT_NORMAL,
			...(this.enemiesDecorativeMode ? TPV_VOLADOR_COLLIDER_DEFAULT_DECORATIVE : {}),
			...(data.tpvCollider ?? {})
		}
		this.cajeroColliderDefaults = {
			...CAJERO_COLLIDER_DEFAULT_NORMAL,
			...(this.enemiesDecorativeMode ? CAJERO_COLLIDER_DEFAULT_DECORATIVE : {}),
			...(data.cajeroCollider ?? {})
		}

		this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
			setPersistentPlayTimeState({ elapsedMs: this.elapsedMapTimeMs })
			this.destroy()
		})
	}

	preload()
	{
		this.load.spritesheet('penquin', 'assets/protagonista.png', {
			frameWidth: 128,
			frameHeight: 128
		})
		this.load.image(LONDON_BACKGROUND_KEY, LONDON_BACKGROUND_PATH)
		this.load.image(BALI_BACKGROUND_KEY, BALI_BACKGROUND_PATH)
		this.load.image(TOKIO_BACKGROUND_KEY, TOKIO_BACKGROUND_PATH)
		this.load.image(this.currentLevel.tilesetImageKey, this.currentLevel.tilesetImagePath)
		this.load.tilemapTiledJSON(this.currentLevel.mapKey, this.currentLevel.mapJsonPath)
		this.load.spritesheet(COIN_SPRITESHEET_KEY, COIN_SPRITESHEET_PATH, {
			frameWidth: COIN_FRAME_WIDTH,
			frameHeight: COIN_FRAME_HEIGHT
		})
		this.load.image(CARD_POWERUP_TEXTURE_KEY, CARD_POWERUP_TEXTURE_PATH)
		this.load.image(BOOMERANG_CARD_TEXTURE_KEY, BOOMERANG_CARD_TEXTURE_PATH)
		this.load.image(INACTIVITY_MODAL_TEXTURE_KEY, INACTIVITY_MODAL_TEXTURE_PATH)
		Object.values(CLOUD_TEXTURES).forEach(cloudTexture => {
			this.load.image(cloudTexture.key, `assets/${cloudTexture.image}`)
		})
		this.load.image(BUILDING_TEXTURES.BigBen, BUILDING_BIG_BEN_IMAGE_PATH)
		this.load.image(BUILDING_TEXTURES['london-bridge'], BUILDING_LONDON_BRIDGE_IMAGE_PATH)
		this.load.image(BUILDING_TEXTURES.bpalace, BUILDING_BPALACE_IMAGE_PATH)
		this.load.image('building-dyn-nube-suelo-png', 'assets/nube-suelo.png')
		this.load.image('building-dyn-bloque-png', 'assets/bloque.png')
		this.load.spritesheet(TPV_VOLADOR_SPRITESHEET_KEY, TPV_VOLADOR_SPRITESHEET_PATH, {
			frameWidth: TPV_VOLADOR_FRAME_WIDTH,
			frameHeight: TPV_VOLADOR_FRAME_HEIGHT
		})
		this.load.spritesheet(ESCANER_SPRITESHEET_KEY, ESCANER_SPRITESHEET_PATH, {
			frameWidth: TPV_VOLADOR_FRAME_WIDTH,
			frameHeight: TPV_VOLADOR_FRAME_HEIGHT
		})
		this.load.spritesheet(CAJERO_VERDE_SPRITESHEET_KEY, CAJERO_VERDE_SPRITESHEET_PATH, {
			frameWidth: CAJERO_FRAME_WIDTH,
			frameHeight: CAJERO_FRAME_HEIGHT
		})
		this.load.spritesheet(CAJERO_AMARILLO_SPRITESHEET_KEY, CAJERO_AMARILLO_SPRITESHEET_PATH, {
			frameWidth: CAJERO_FRAME_WIDTH,
			frameHeight: CAJERO_FRAME_HEIGHT
		})
		LEVEL_BACKGROUND_MUSIC_KEYS.forEach((musicKey, index) => {
			this.load.audio(musicKey, LEVEL_BACKGROUND_MUSIC_PATHS[index])
		})
		this.load.audio(FINAL_BOSS_MUSIC_KEY, FINAL_BOSS_MUSIC_PATH)
		this.load.audio(SFX_ENEMY_DESTROYED_KEY, SFX_ENEMY_DESTROYED_PATH)
		this.load.audio(SFX_PLAYER_HIT_KEY, SFX_PLAYER_HIT_PATH)
		this.load.audio(SFX_COIN_COLLECTED_KEY, SFX_COIN_COLLECTED_PATH)
		this.load.audio(SFX_CARD_POWERUP_COLLECTED_KEY, SFX_CARD_POWERUP_COLLECTED_PATH)
		this.load.audio(SFX_JUMP_KEY, SFX_JUMP_PATH)
			this.load.spritesheet(FINAL_BOSS_SPRITESHEET_KEY, FINAL_BOSS_SPRITESHEET_PATH, {
				frameWidth: FINAL_BOSS_FRAME_WIDTH,
				frameHeight: FINAL_BOSS_FRAME_HEIGHT
			})

		const transitionVideoPath = typeof this.currentLevel.videoPath === 'string' && this.currentLevel.videoPath.trim().length > 0
			? this.currentLevel.videoPath.trim()
			: undefined
		if (transitionVideoPath)
		{
			const transitionVideoCacheKey = getLevelTransitionVideoCacheKey(transitionVideoPath)
			if (!this.cache.video.exists(transitionVideoCacheKey))
			{
				this.load.video(transitionVideoCacheKey, transitionVideoPath, 'canplaythrough', false, false)
			}
		}
	}

	private playLoopedMusic(musicKey: string)
	{
		if (this.levelMusic)
		{
			this.levelMusic.stop()
			this.levelMusic.destroy()
			this.levelMusic = undefined
		}

		if (!this.cache.audio.exists(musicKey))
		{
			return
		}

		this.levelMusic = this.sound.add(musicKey, {
			loop: true,
			volume: LEVEL_BACKGROUND_MUSIC_VOLUME
		})
		this.levelMusic.play()
		this.applyLevelMusicPlaybackRate(this.levelCriticalMusicActive
			? LEVEL_BACKGROUND_MUSIC_RATE_CRITICAL
			: LEVEL_BACKGROUND_MUSIC_RATE_NORMAL)
	}

	private applyLevelMusicPlaybackRate(rate: number)
	{
		if (!this.levelMusic)
		{
			return
		}

		const safeRate = Math.max(0.1, rate)
		const rateCapableSound = this.levelMusic as Phaser.Sound.BaseSound & {
			setRate?: (value: number) => Phaser.Sound.BaseSound
		}
		if (typeof rateCapableSound.setRate === 'function')
		{
			rateCapableSound.setRate(safeRate)
		}
	}

	private syncLevelCriticalMusicState(remainingSeconds: number)
	{
		const shouldUseCriticalRate = this.levelTimeActive
			&& remainingSeconds > 0
			&& remainingSeconds <= LEVEL_TIME_CRITICAL_THRESHOLD_SECONDS

		if (shouldUseCriticalRate === this.levelCriticalMusicActive)
		{
			return
		}

		this.levelCriticalMusicActive = shouldUseCriticalRate
		this.applyLevelMusicPlaybackRate(
			shouldUseCriticalRate
				? LEVEL_BACKGROUND_MUSIC_RATE_CRITICAL
				: LEVEL_BACKGROUND_MUSIC_RATE_NORMAL
		)
	}

	private configureAudioRegistry()
	{
		this.game.registry.set('sfxVolume', ACTION_SFX_VOLUME)
		this.game.registry.set('sfxKeyEnemyDestroyed', SFX_ENEMY_DESTROYED_KEY)
		this.game.registry.set('sfxKeyPlayerHit', SFX_PLAYER_HIT_KEY)
		this.game.registry.set('sfxKeyCoinCollected', SFX_COIN_COLLECTED_KEY)
		this.game.registry.set('sfxKeyCardPowerupCollected', SFX_CARD_POWERUP_COLLECTED_KEY)
		this.game.registry.set('sfxKeyJump', SFX_JUMP_KEY)
	}

	private startLevelBackgroundMusic()
	{
		const muteLevelMusic = this.game.registry.get('muteLevelMusic') === true
		if (muteLevelMusic)
		{
			return
		}

		const musicKey = LEVEL_BACKGROUND_MUSIC_KEYS[this.currentLevelIndex]
		if (!musicKey)
		{
			return
		}

		this.playLoopedMusic(musicKey)
		this.syncLevelCriticalMusicState(Math.max(0, Math.floor(this.levelTimeRemainingMs / 1000)))
	}

	private startFinalBossMusic()
	{
		if (this.finalBossMusicTriggered)
		{
			return
		}

		this.finalBossMusicTriggered = true
		this.playLoopedMusic(FINAL_BOSS_MUSIC_KEY)
	}

	private ensureCoinAnimation()
	{
		if (this.anims.exists('coin-spin'))
		{
			return
		}

		this.anims.create({
			key: 'coin-spin',
			frames: this.anims.generateFrameNumbers(COIN_SPRITESHEET_KEY, {
				start: 0,
				end: 7
			}),
			frameRate: 12,
			repeat: -1
		})
	}

	private ensureFinalBossAnimations()
	{
		if (!this.anims.exists(FINAL_BOSS_ANIM_WALK_LEFT))
		{
			this.anims.create({
				key: FINAL_BOSS_ANIM_WALK_LEFT,
				frames: this.anims.generateFrameNumbers(FINAL_BOSS_SPRITESHEET_KEY, {
					start: 0,
					end: 7
				}),
				frameRate: 10,
				repeat: -1
			})
		}

		if (!this.anims.exists(FINAL_BOSS_ANIM_WALK_RIGHT))
		{
			this.anims.create({
				key: FINAL_BOSS_ANIM_WALK_RIGHT,
				frames: this.anims.generateFrameNumbers(FINAL_BOSS_SPRITESHEET_KEY, {
					start: 8,
					end: 15
				}),
				frameRate: 10,
				repeat: -1
			})
		}

		if (!this.anims.exists(FINAL_BOSS_ANIM_HIT_LEFT))
		{
			this.anims.create({
				key: FINAL_BOSS_ANIM_HIT_LEFT,
				frames: this.anims.generateFrameNumbers(FINAL_BOSS_SPRITESHEET_KEY, {
					start: 16,
					end: 21
				}),
				frameRate: 14,
				repeat: 0
			})
		}

		if (!this.anims.exists(FINAL_BOSS_ANIM_HIT_RIGHT))
		{
			this.anims.create({
				key: FINAL_BOSS_ANIM_HIT_RIGHT,
				frames: this.anims.generateFrameNumbers(FINAL_BOSS_SPRITESHEET_KEY, {
					start: 24,
					end: 29
				}),
				frameRate: 14,
				repeat: 0
			})
		}
	}

	private readObjectNumberProperty(objData: Phaser.Types.Tilemaps.TiledObject, propertyName: string): number | undefined
	{
		const properties = objData.properties as Array<{ name: string, value: unknown }> | undefined
		if (!properties || properties.length === 0)
		{
			return undefined
		}

		const property = properties.find(item => item.name === propertyName)
		if (!property)
		{
			return undefined
		}

		if (typeof property.value === 'number')
		{
			return property.value
		}

		if (typeof property.value === 'string')
		{
			const parsed = Number(property.value)
			return Number.isFinite(parsed) ? parsed : undefined
		}

		return undefined
	}

	private readObjectNumberProperties(objData: Phaser.Types.Tilemaps.TiledObject, propertyNames: string[]): number | undefined
	{
		for (const propertyName of propertyNames)
		{
			const value = this.readObjectNumberProperty(objData, propertyName)
			if (value !== undefined)
			{
				return value
			}
		}

		return undefined
	}

	private readObjectStringProperty(objData: Phaser.Types.Tilemaps.TiledObject, propertyName: string): string | undefined
	{
		const properties = objData.properties as Array<{ name: string, value: unknown }> | undefined
		if (!properties || properties.length === 0)
		{
			return undefined
		}

		const property = properties.find(item => item.name === propertyName)
		if (!property || typeof property.value !== 'string')
		{
			return undefined
		}

		return property.value.trim() || undefined
	}

	private readObjectBooleanProperty(objData: Phaser.Types.Tilemaps.TiledObject, propertyName: string): boolean | undefined
	{
		const properties = objData.properties as Array<{ name: string, value: unknown }> | undefined
		if (!properties || properties.length === 0)
		{
			return undefined
		}

		const property = properties.find(item => item.name === propertyName)
		if (!property)
		{
			return undefined
		}

		if (typeof property.value === 'boolean')
		{
			return property.value
		}

		if (typeof property.value === 'number')
		{
			return property.value !== 0
		}

		if (typeof property.value === 'string')
		{
			const normalized = property.value.trim().toLowerCase()
			if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'si')
			{
				return true
			}

			if (normalized === 'false' || normalized === '0' || normalized === 'no')
			{
				return false
			}
		}

		return undefined
	}

	private resolveCoinReward(objData: Phaser.Types.Tilemaps.TiledObject): CoinRewardSpec
	{
		const coinTypeFromProperty = this.readObjectStringProperty(objData, 'type')
		const coinTypeFromObject = typeof objData.type === 'string'
			? objData.type.trim()
			: ''
		const resolvedType = (coinTypeFromProperty || coinTypeFromObject || COIN_TYPE_BIZUM).toLowerCase()

		return {
			coinType: resolvedType,
			budgetPoints: COIN_BUDGET_BY_TYPE[resolvedType] ?? DEFAULT_COIN_BUDGET_POINTS
		}
	}

	private isBlockingStreetFurniture(objData: Phaser.Types.Tilemaps.TiledObject, groupName: string): boolean
	{
		if (groupName !== 'Mobiliario')
		{
			return false
		}

		const explicitBlocking = this.readObjectBooleanProperty(objData, 'blocking')
		if (explicitBlocking !== undefined)
		{
			return explicitBlocking
		}

		const objectName = (objData.name ?? '').trim().toLowerCase()
		if (BLOCKING_STREET_FURNITURE_NAMES.some(blockingName => objectName === blockingName || objectName.startsWith(blockingName)))
		{
			return true
		}

		const defaultImage = DEFAULT_OBJECT_IMAGES[objectName]
			?? (objectName.startsWith('bloque') ? 'bloque.png' : undefined)
			?? (objectName.startsWith('nube-suelo') ? 'nube-suelo.png' : undefined)
		const configuredImage = (this.readObjectStringProperty(objData, 'image') ?? defaultImage ?? '')
			.trim()
			.toLowerCase()
		if (!configuredImage)
		{
			return false
		}

		const fileName = configuredImage.split('/').pop() ?? configuredImage
		return BLOCKING_STREET_FURNITURE_NAMES.some(blockingName => fileName.startsWith(blockingName))
	}

	private resolveEnemyColliderConfig(objData: Phaser.Types.Tilemaps.TiledObject, defaults: EnemyColliderConfig): EnemyColliderConfig
	{
		const widthRatio = this.readObjectNumberProperties(objData, ['colliderWidthRatio', 'enemyColliderWidthRatio'])
		const heightRatio = this.readObjectNumberProperties(objData, ['colliderHeightRatio', 'enemyColliderHeightRatio'])
		const bottomOffset = this.readObjectNumberProperties(objData, ['colliderBottomOffset', 'enemyColliderBottomOffset', 'colliderBottom'])

		return {
			widthRatio: Phaser.Math.Clamp(widthRatio ?? defaults.widthRatio, 0.1, 1),
			heightRatio: Phaser.Math.Clamp(heightRatio ?? defaults.heightRatio, 0.1, 1),
			bottomOffset: Math.max(0, bottomOffset ?? defaults.bottomOffset)
		}
	}

	private applyEnemyCollider(sprite: Phaser.Physics.Matter.Sprite, colliderConfig: EnemyColliderConfig)
	{
		const sourceWidth = sprite.displayWidth
		const sourceHeight = sprite.displayHeight

		const colliderWidth = Math.max(1, sourceWidth * colliderConfig.widthRatio)
		const colliderHeight = Math.max(1, sourceHeight * colliderConfig.heightRatio)
		const desiredOriginY = sourceHeight - colliderConfig.bottomOffset - (colliderHeight * 0.5)

		const body = this.matter.bodies.rectangle(
			sprite.x,
			sprite.y,
			colliderWidth,
			colliderHeight
		)

		sprite.setExistingBody(body)
		sprite.setDisplayOrigin(sourceWidth * 0.5, desiredOriginY)
	}

	private setupDecorativeEnemyBody(sprite: Phaser.Physics.Matter.Sprite)
	{
		sprite.setStatic(true)
		sprite.setSensor(true)
		sprite.setIgnoreGravity(true)
		sprite.setVelocity(0, 0)
		sprite.setAngularVelocity(0)

		const body = sprite.body as MatterJS.BodyType
		body.collisionFilter.mask = ENEMY_DECORATIVE_NO_COLLISION_MASK
	}

	private resolveRandomTravelDistance(
		objData: Phaser.Types.Tilemaps.TiledObject,
		defaultMin: number,
		defaultMax: number
	): number
	{
		const explicitDistance = this.readObjectNumberProperties(objData, ['travelDistance', 'distance', 'distancia'])
		if (explicitDistance !== undefined)
		{
			return Math.max(0, explicitDistance)
		}

		const configuredMin = this.readObjectNumberProperties(objData, [
			'travelDistanceMin',
			'distanceMin',
			'distanciaMin',
			'patrolMinDistance'
		])
		const configuredMax = this.readObjectNumberProperties(objData, [
			'travelDistanceMax',
			'distanceMax',
			'distanciaMax',
			'patrolMaxDistance'
		])

		const minDistance = Math.max(0, configuredMin ?? defaultMin)
		const maxDistance = Math.max(minDistance, configuredMax ?? defaultMax)

		if (maxDistance === minDistance)
		{
			return minDistance
		}

		return Phaser.Math.Between(Math.floor(minDistance), Math.floor(maxDistance))
	}

	private resolveCloudSpec(objData: Phaser.Types.Tilemaps.TiledObject): MovingCloudSpec | undefined
	{
		const { x = 0, y = 0, name = '', width = 0, height = 0 } = objData
		const configuredImage = this.readObjectStringProperty(objData, 'image')

		if (configuredImage)
		{
			const normalized = configuredImage.replace(/^assets\//, '')
			const texturePath = normalized.startsWith('assets/') ? normalized : `assets/${normalized}`
			const textureKey = `cloud-dyn-${normalized.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

			return {
				x,
				y,
				width,
				height,
				textureKey,
				texturePath
			}
		}

		const mappedTextureKey = CLOUD_TEXTURES[name as keyof typeof CLOUD_TEXTURES]
		if (!mappedTextureKey)
		{
			return undefined
		}

		return {
			x,
			y,
			width,
			height,
			textureKey: mappedTextureKey.key
		}
	}

	private resolveObjectTextureSource(configuredImage: string, keyPrefix: string): { textureKey: string, texturePath: string } | undefined
	{
		const imageValue = configuredImage.trim()
		if (!imageValue)
		{
			return undefined
		}

		const isAbsoluteUrl = /^(https?:)?\/\//i.test(imageValue)
			|| imageValue.startsWith('data:')
			|| imageValue.startsWith('blob:')

		const normalizedAssetPath = imageValue.replace(/^\/?assets\//i, '')
		const texturePath = isAbsoluteUrl ? imageValue : `assets/${normalizedAssetPath}`
		const keySeed = (isAbsoluteUrl ? imageValue : normalizedAssetPath)
			.toLowerCase()
			.replace(/\?.*$/, '')
			.replace(/#.*$/, '')
		const textureKey = `${keyPrefix}-${keySeed.replace(/[^a-z0-9]+/g, '-')}`

		return {
			textureKey,
			texturePath
		}
	}

	private createCardPowerupPickup(objData: Phaser.Types.Tilemaps.TiledObject)
	{
		const { x = 0, y = 0, width = 0, height = 0 } = objData
		const configuredImage = this.readObjectStringProperty(objData, 'image')
			?? this.readObjectStringProperty(objData, 'images')
		const textureSource = configuredImage
			? this.resolveObjectTextureSource(configuredImage, 'card-powerup-dyn')
			: undefined
		const resolvedTextureKey = textureSource && this.textures.exists(textureSource.textureKey)
			? textureSource.textureKey
			: CARD_POWERUP_TEXTURE_KEY
		const powerupWidth = width > 0 ? width : CARD_POWERUP_DEFAULT_WIDTH
		const powerupHeight = height > 0 ? height : CARD_POWERUP_DEFAULT_HEIGHT
		const powerup = this.matter.add.sprite(
			x + (powerupWidth * 0.5),
			y + (powerupHeight * 0.5),
			resolvedTextureKey,
			undefined,
			{
				isStatic: true,
				isSensor: true
			}
		)
		powerup.setDisplaySize(powerupWidth, powerupHeight)
		powerup.setDepth(GAMEPLAY_RENDER_DEPTH)
		powerup.setData('type', CARD_POWERUP_OBJECT_TYPE)

		const floatTween = this.tweens.add({
			targets: powerup,
			y: powerup.y - CARD_POWERUP_FLOAT_OFFSET_PX,
			duration: CARD_POWERUP_FLOAT_DURATION_MS,
			ease: 'Sine.easeInOut',
			yoyo: true,
			repeat: -1
		})
		powerup.setData('floatTween', floatTween)
		powerup.once(Phaser.GameObjects.Events.DESTROY, () => {
			if (floatTween.isPlaying())
			{
				floatTween.stop()
			}
		})
	}

	private resolveFinishVisualSpec(objData: Phaser.Types.Tilemaps.TiledObject): StaticBuildingSpec | undefined
	{
		const configuredImage = this.readObjectStringProperty(objData, 'image')
		if (!configuredImage)
		{
			return undefined
		}

		const textureSource = this.resolveObjectTextureSource(configuredImage, 'finish-dyn')
		if (!textureSource)
		{
			return undefined
		}

		const { x = 0, y = 0, width = 0, height = 0 } = objData
		return {
			x,
			y,
			width,
			height,
			textureKey: textureSource.textureKey,
			texturePath: textureSource.texturePath,
			renderDepth: GAMEPLAY_RENDER_DEPTH - 1,
			isBlocking: false,
			blockingTopTrimRatio: 0,
			blockingChamferRatio: 0,
			blockingChamferMaxPx: 0,
			blockingBottomOnlyChamfer: false,
			isParallaxDriftCandidate: false
		}
	}

	private createMovingCloud(spec: MovingCloudSpec)
	{
		if (!this.textures.exists(spec.textureKey))
		{
			return
		}

		const textureSource = this.textures.get(spec.textureKey).getSourceImage() as { width: number, height: number }
		const cloudWidth = spec.width > 0 ? spec.width : textureSource.width
		const cloudHeight = spec.height > 0 ? spec.height : textureSource.height
		const cloud = this.add.image(spec.x + (cloudWidth * 0.5), spec.y + (cloudHeight * 0.5), spec.textureKey)
		cloud.setDisplaySize(cloudWidth, cloudHeight)
		cloud.setDepth(CLOUD_RENDER_DEPTH)
		cloud.setScrollFactor(this.cloudParallaxFactorX, 1)

		this.movingClouds.push({
			sprite: cloud
		})
	}

	private isLevel2BuildingParallaxEnabled(): boolean
	{
		return this.currentLevel.mapKey === BALI_MAP_KEY
	}

	private createMovingBuilding(building: Phaser.GameObjects.Image)
	{
		// Keep building height anchored to map coordinates; parallax should only affect X.
		building.setScrollFactor(this.buildingParallaxFactorX, 1)

		this.movingBuildings.push({
			sprite: building
		})
	}

	private resolveBuildingSpec(objData: Phaser.Types.Tilemaps.TiledObject, groupName: string): StaticBuildingSpec | undefined
	{
		const { x = 0, y = 0, name = '', width = 0, height = 0 } = objData
		const objectName = (name ?? '').trim().toLowerCase()
		const defaultImage = DEFAULT_OBJECT_IMAGES[objectName]
			?? (objectName.startsWith('bloque') ? 'bloque.png' : undefined)
			?? (objectName.startsWith('nube-suelo') ? 'nube-suelo.png' : undefined)
		const configuredImage = this.readObjectStringProperty(objData, 'image')
			?? this.readObjectStringProperty(objData, 'images')
			?? defaultImage
		const isBlocksGroup = groupName === 'Bloques'
		const renderDepth = isBlocksGroup
			? BLOCK_RENDER_DEPTH
			: (groupName === 'Mobiliario' ? STREET_FURNITURE_RENDER_DEPTH : BUILDING_RENDER_DEPTH)
		const isBlocking = isBlocksGroup || this.isBlockingStreetFurniture(objData, groupName)
		const isNubeSuelo = objectName === 'nube-suelo' || objectName.startsWith('nube-suelo')
		const isBloque = objectName === 'bloque' || objectName.startsWith('bloque')
		const topTrimRatio = Phaser.Math.Clamp(
			this.readObjectNumberProperties(objData, ['colliderTopTrimRatio', 'blockingTopTrimRatio'])
				?? (isNubeSuelo ? NUBE_SUELO_COLLIDER_TOP_TRIM_RATIO : 0),
			0,
			0.9
		)
		const chamferRatio = Math.max(
			0,
			this.readObjectNumberProperties(objData, ['colliderChamferRatio', 'blockingChamferRatio'])
				?? (isNubeSuelo || isBloque ? NUBE_SUELO_CHAMFER_RATIO : BLOCKING_STREET_FURNITURE_CHAMFER_RATIO)
		)
		const chamferMaxPx = Math.max(
			0,
			this.readObjectNumberProperties(objData, ['colliderChamferMaxPx', 'blockingChamferMaxPx'])
				?? (isNubeSuelo || isBloque ? NUBE_SUELO_CHAMFER_MAX_PX : BLOCKING_STREET_FURNITURE_CHAMFER_MAX_PX)
		)
		const bottomOnlyChamfer = isBlocksGroup

		if (configuredImage)
		{
			const normalized = configuredImage.replace(/^assets\//, '')
			const texturePath = normalized.startsWith('assets/') ? normalized : `assets/${normalized}`
			const textureKey = `building-dyn-${normalized.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

			return {
				x,
				y,
				width,
				height,
				textureKey,
				texturePath,
				renderDepth,
				isBlocking,
				blockingTopTrimRatio: isBlocksGroup ? 0 : topTrimRatio,
				blockingChamferRatio: isBlocksGroup ? BLOCKING_PLATFORM_BOTTOM_CHAMFER_RATIO : chamferRatio,
				blockingChamferMaxPx: isBlocksGroup ? BLOCKING_PLATFORM_BOTTOM_CHAMFER_MAX_PX : chamferMaxPx,
				blockingBottomOnlyChamfer: bottomOnlyChamfer,
				isParallaxDriftCandidate: groupName === 'Edificios'
			}
		}

		const mappedTextureKey = BUILDING_TEXTURES[name as keyof typeof BUILDING_TEXTURES]
		if (!mappedTextureKey)
		{
			return undefined
		}

		return {
			x,
			y,
			width,
			height,
			textureKey: mappedTextureKey,
			renderDepth,
			isBlocking,
			blockingTopTrimRatio: topTrimRatio,
			blockingChamferRatio: chamferRatio,
			blockingChamferMaxPx: chamferMaxPx,
			blockingBottomOnlyChamfer: bottomOnlyChamfer,
			isParallaxDriftCandidate: groupName === 'Edificios'
		}
	}

	private ensureVisualTextures(specs: Array<{ textureKey: string, texturePath?: string }>, onComplete: () => void)
	{
		const pendingLoads = new Map<string, string>()

		specs.forEach(spec => {
			if (!spec.texturePath)
			{
				return
			}

			if (this.textures.exists(spec.textureKey))
			{
				return
			}

			pendingLoads.set(spec.textureKey, spec.texturePath)
		})

		if (pendingLoads.size === 0)
		{
			onComplete()
			return
		}

		pendingLoads.forEach((texturePath, textureKey) => {
			this.load.image(textureKey, texturePath)
		})

		this.load.once(Phaser.Loader.Events.COMPLETE, () => {
			onComplete()
		})

		this.load.start()
	}

	private createStaticBuilding(spec: StaticBuildingSpec)
	{
		if (!this.textures.exists(spec.textureKey))
		{
			return
		}

		const textureSource = this.textures.get(spec.textureKey).getSourceImage() as { width: number, height: number }
		const buildingWidth = spec.width > 0 ? spec.width : textureSource.width
		const buildingHeight = spec.height > 0 ? spec.height : textureSource.height
		const isLevel2ParallaxBuilding = spec.isParallaxDriftCandidate && this.isLevel2BuildingParallaxEnabled()
		const building = this.add.image(spec.x + (buildingWidth * 0.5), spec.y + (buildingHeight * 0.5), spec.textureKey)
		building.setDisplaySize(buildingWidth, buildingHeight)
		building.setDepth(spec.renderDepth)
		if (isLevel2ParallaxBuilding)
		{
			this.createMovingBuilding(building)
		}
		this.staticBuildings.push(building)

		if (spec.isBlocking)
		{
			const colliderHeight = Math.max(1, building.displayHeight * (1 - spec.blockingTopTrimRatio))
			const colliderYOffset = (building.displayHeight - colliderHeight) * 0.5
			const chamferRadius = Phaser.Math.Clamp(
				Math.min(building.displayWidth, colliderHeight) * spec.blockingChamferRatio,
				0,
				spec.blockingChamferMaxPx
			)
			const chamferRadiusByCorner = spec.blockingBottomOnlyChamfer
				? [0, 0, chamferRadius, chamferRadius]
				: chamferRadius

			const blockingBody = this.matter.add.rectangle(
				building.x,
				building.y + colliderYOffset,
				building.displayWidth,
				colliderHeight,
				{
					isStatic: true,
					chamfer: {
						radius: chamferRadiusByCorner
					}
				}
			)
			blockingBody.collisionFilter.category = GROUND_COLLISION_CATEGORY

			this.obstacles.add('street-furniture', blockingBody as MatterJS.BodyType)
		}
	}

	create()
	{
		// Keep sub-pixel rendering in this scene so slow cloud drift looks smooth.
		this.cameras.main.roundPixels = false
		this.configureAudioRegistry()
		this.startLevelBackgroundMusic()

		this.scene.launch('ui')
		setLogoOverlayVisible(false)
		if (this.scene.isActive('logo-overlay'))
		{
			this.scene.stop('logo-overlay')
		}
		this.createInactivityModal()
		this.bindInactivityInput()
		this.enableInactivityWatchdog()
		this.bindRankingEventForwarders()
		void this.ensureRunSessionStarted()
		this.ensureCoinAnimation()
		this.ensureFinalBossAnimations()
		this.emitPlayTimeChanged()
		this.emitLevelTimeChanged()
		this.destroyFinalBoss(false)

		const map = this.make.tilemap({ key: this.currentLevel.mapKey })
		this.currentMapWidthPx = map.widthInPixels
		this.currentMapHeightPx = map.heightInPixels
		this.currentMapTileWidthPx = map.tileWidth
		this.currentMapTileHeightPx = map.tileHeight
		this.setupLevelThreeFinalBlocker(map)
		const tileset = map.addTilesetImage(this.currentLevel.tilesetName, this.currentLevel.tilesetImageKey)
		if (!tileset)
		{
			throw new Error(`No se pudo cargar el tileset ${this.currentLevel.tilesetName} para el nivel ${this.currentLevelIndex + 1}`)
		}

		const activeBackgroundKey = this.currentLevel.mapKey === LONDON_MAP_KEY
			? LONDON_BACKGROUND_KEY
			: (this.currentLevel.mapKey === BALI_MAP_KEY
				? BALI_BACKGROUND_KEY
				: (this.currentLevel.mapKey === TOKIO_MAP_KEY ? TOKIO_BACKGROUND_KEY : undefined))

		if (activeBackgroundKey)
		{
			const backgroundSource = this.textures.get(activeBackgroundKey).getSourceImage() as { width: number, height: number }

			this.londonBackground = this.add.tileSprite(
				0,
				0,
				this.scale.width,
				this.scale.height,
				activeBackgroundKey
			)
				.setOrigin(0)
				.setScrollFactor(0)
				.setDepth(-1000)

			const cameraTravelX = Math.max(1, map.widthInPixels - this.scale.width)
			const cameraTravelY = Math.max(1, map.heightInPixels - this.scale.height)
			const backgroundTravelX = Math.max(0, backgroundSource.width - this.scale.width)
			const backgroundTravelY = Math.max(0, backgroundSource.height - this.scale.height)

			this.londonParallaxFactorX = backgroundTravelX > 0
				? backgroundTravelX / cameraTravelX
				: LONDON_PARALLAX_SCROLL_FACTOR_X

			this.londonParallaxFactorY = backgroundTravelY > 0
				? backgroundTravelY / cameraTravelY
				: LONDON_PARALLAX_SCROLL_FACTOR_Y

			this.cloudParallaxFactorX = Math.min(CLOUD_PARALLAX_MAX_FACTOR, this.londonParallaxFactorX * CLOUD_PARALLAX_SPEED_MULTIPLIER_X)
			this.cloudParallaxFactorY = Math.min(CLOUD_PARALLAX_MAX_FACTOR, this.londonParallaxFactorY * CLOUD_PARALLAX_SPEED_MULTIPLIER_Y)
			this.buildingParallaxFactorX = Math.max(BUILDING_PARALLAX_MIN_FACTOR_X, this.londonParallaxFactorX)

			this.londonResizeHandler = gameSize => {
				if (!this.londonBackground)
				{
					return
				}

				this.londonBackground.setSize(gameSize.width, gameSize.height)

				const resizedCameraTravelX = Math.max(1, map.widthInPixels - gameSize.width)
				const resizedCameraTravelY = Math.max(1, map.heightInPixels - gameSize.height)
				const resizedBackgroundTravelX = Math.max(0, backgroundSource.width - gameSize.width)
				const resizedBackgroundTravelY = Math.max(0, backgroundSource.height - gameSize.height)

				this.londonParallaxFactorX = resizedBackgroundTravelX > 0
					? resizedBackgroundTravelX / resizedCameraTravelX
					: LONDON_PARALLAX_SCROLL_FACTOR_X

				this.londonParallaxFactorY = resizedBackgroundTravelY > 0
					? resizedBackgroundTravelY / resizedCameraTravelY
					: LONDON_PARALLAX_SCROLL_FACTOR_Y

				this.cloudParallaxFactorX = Math.min(CLOUD_PARALLAX_MAX_FACTOR, this.londonParallaxFactorX * CLOUD_PARALLAX_SPEED_MULTIPLIER_X)
				this.cloudParallaxFactorY = Math.min(CLOUD_PARALLAX_MAX_FACTOR, this.londonParallaxFactorY * CLOUD_PARALLAX_SPEED_MULTIPLIER_Y)
				this.buildingParallaxFactorX = Math.max(BUILDING_PARALLAX_MIN_FACTOR_X, this.londonParallaxFactorX)
				this.movingClouds.forEach(cloud => {
					cloud.sprite.setScrollFactor(this.cloudParallaxFactorX, 1)
				})
				this.movingBuildings.forEach(building => {
					building.sprite.setScrollFactor(this.buildingParallaxFactorX, 1)
				})
			}

			this.scale.on(Phaser.Scale.Events.RESIZE, this.londonResizeHandler)
		}

		const ground = map.createLayer('ground', tileset)
		if (ground)
		{
			// Fallback: todo tile pintado en ground colisiona, aunque no tenga propiedad collides.
			ground.setCollisionByExclusion([-1])
			this.matter.world.convertTilemapLayer(ground)
			const groundCollisionTileFactor = this.currentLevel.groundCollisionTileFactor ?? DEFAULT_GROUND_COLLISION_TILE_FACTOR
			const groundCollisionYOffset = map.tileHeight * groundCollisionTileFactor
			const offsetStartTileY = Math.max(0, map.height - GROUND_COLLISION_OFFSET_ROWS_FROM_BOTTOM)
			let movedBodies = 0
			let skippedBodies = 0

			this.matter.world.engine.world.bodies.forEach(body => {
				const maybeTileBody = body.gameObject as { tile?: Phaser.Tilemaps.Tile } | undefined
				if (!maybeTileBody?.tile)
				{
					return
				}

				;(body as MatterJS.BodyType & { hasBottomGroundOffset?: boolean }).hasBottomGroundOffset = false

				body.collisionFilter.category = GROUND_COLLISION_CATEGORY

				if (groundCollisionYOffset === 0)
				{
					return
				}

				if (maybeTileBody.tile.y < offsetStartTileY)
				{
					++skippedBodies
					return
				}

				Phaser.Physics.Matter.Matter.Body.setPosition(body, {
					x: body.position.x,
					y: body.position.y + groundCollisionYOffset
				})
				;(body as MatterJS.BodyType & { hasBottomGroundOffset?: boolean }).hasBottomGroundOffset = true

				++movedBodies
			})

			if (groundCollisionYOffset !== 0)
			{
				// console.info(`[ground-collision-offset] factor=${groundCollisionTileFactor} yOffset=${groundCollisionYOffset} fromTileY=${offsetStartTileY} bottomRows=${GROUND_COLLISION_OFFSET_ROWS_FROM_BOTTOM} movedBodies=${movedBodies} skippedBodies=${skippedBodies}`)
			}
		}

		map.layers.forEach(layerData => {
			if (layerData.name === 'ground')
			{
				return
			}

			map.createLayer(layerData.name, tileset)
		})

		const objectsLayer = map.getObjectLayer('objects')
		if (!objectsLayer)
		{
			throw new Error('No existe la capa objects en el tilemap')
		}

		const objectLayers = map.objects.filter(layer => layer.type === 'objectgroup')
		const pendingBuildings: StaticBuildingSpec[] = []
		const pendingClouds: MovingCloudSpec[] = []
		objectLayers.forEach(layer => {
			layer.objects.forEach(objData => {
			const { x = 0, y = 0, name, width = 0, height = 0 } = objData

			if (layer.name === 'Fin' && this.levelEndTriggerX === undefined)
			{
				this.levelEndTriggerX = x + (width * 0.5)
			}

			if (layer.name === 'Fin')
			{
				const finishVisualSpec = this.resolveFinishVisualSpec(objData)
				if (finishVisualSpec)
				{
					pendingBuildings.push(finishVisualSpec)
				}
				return
			}
           
			switch (name)
			{
				case 'penquin-spawn':
				{
                    const x = START_PLAYER_Y;
					this.penquin = this.matter.add.sprite(x + (width * 0.5), y, 'penquin')
						.setFixedRotation()
						.setDepth(GAMEPLAY_RENDER_DEPTH)

					this.playerController = new PlayerController(
						this,
						this.penquin,
						this.cursors,
						this.obstacles,
						this.pendingLevelStartBudgetState
					)

					this.cameras.main.startFollow(this.penquin, true)
					this.cameras.main.setBounds(0, 0, map.widthInPixels, map.heightInPixels)
					this.matter.world.setBounds(0, 0, map.widthInPixels, map.heightInPixels)

					const CAMERA_ZOOM = 1
					this.cameras.main.setZoom(CAMERA_ZOOM)
					break
				}

				case 'tpv_volador':
				case 'escaner':
				{
					const isEscaner = name === 'escaner'
					const enemyTextureKey = isEscaner
						? ESCANER_SPRITESHEET_KEY
						: TPV_VOLADOR_SPRITESHEET_KEY
					const enemyAnimationPrefix = isEscaner
						? 'escaner'
						: 'tpv-volador'
					const defaultTravelDistanceMin = isEscaner
						? ESCANER_DEFAULT_TRAVEL_DISTANCE_MIN
						: TPV_VOLADOR_DEFAULT_TRAVEL_DISTANCE_MIN
					const defaultTravelDistanceMax = isEscaner
						? ESCANER_DEFAULT_TRAVEL_DISTANCE_MAX
						: TPV_VOLADOR_DEFAULT_TRAVEL_DISTANCE_MAX
					const defaultMoveSpeed = isEscaner ? ESCANER_DEFAULT_MOVE_SPEED : undefined
					const enemyWidth = width > 0 ? width : TPV_VOLADOR_FRAME_WIDTH
					const enemyHeight = height > 0 ? height : TPV_VOLADOR_FRAME_HEIGHT

					if (this.enemiesDecorativeMode)
					{
						const tpvDecorative = this.matter.add.sprite(
							x + (enemyWidth * 0.5),
							y + (enemyHeight * 0.5),
							enemyTextureKey,
							0
						)
							.setDisplaySize(enemyWidth, enemyHeight)
							.setDepth(GAMEPLAY_RENDER_DEPTH)
						const tpvCollider = this.resolveEnemyColliderConfig(objData, this.tpvVoladorColliderDefaults)
						this.applyEnemyCollider(tpvDecorative, tpvCollider)
						this.setupDecorativeEnemyBody(tpvDecorative)
						break
					}

					const tpvVolador = this.matter.add.sprite(
						x + (enemyWidth * 0.5),
						y + (enemyHeight * 0.5),
						enemyTextureKey,
						0
					)
						.setDisplaySize(enemyWidth, enemyHeight)
						.setDepth(GAMEPLAY_RENDER_DEPTH)
					const tpvCollider = this.resolveEnemyColliderConfig(objData, this.tpvVoladorColliderDefaults)
					this.applyEnemyCollider(tpvVolador, tpvCollider)
					tpvVolador.setFixedRotation()
					tpvVolador.setIgnoreGravity(true)
					tpvVolador.setData('enemyHitBudgetLoss', TPV_VOLADOR_ENEMY_HIT_BUDGET_LOSS)

					const travelDistance = this.resolveRandomTravelDistance(
						objData,
						defaultTravelDistanceMin,
						defaultTravelDistanceMax
					)
					const moveSpeed = this.readObjectNumberProperties(objData, ['speed', 'velocidad'])
						?? defaultMoveSpeed
					const nonGroundLandingYOffset = this.readObjectNumberProperties(objData, [
						'nonGroundLandingYOffset',
						'platformLandingYOffset',
						'landingYOffset',
						'deathLandingYOffset'
					])

					this.tpvVoladores.push(new TpvVoladorController(this, tpvVolador, {
						startX: tpvVolador.x,
						travelDistance,
						moveSpeed,
						nonGroundLandingYOffset,
						initialDirection: Math.random() < 0.5 ? -1 : 1,
						spritesheetKey: enemyTextureKey,
						animationKeyPrefix: enemyAnimationPrefix
					}))

					this.obstacles.add('tpv-volador', tpvVolador.body as MatterJS.BodyType)
					break
				}

				case 'cajero_verde':
				case 'cajero_amarillo':
				case 'tpv_amarillo':
				{
					const enemyWidth = width > 0 ? width : CAJERO_FRAME_WIDTH
					const enemyHeight = height > 0 ? height : CAJERO_FRAME_HEIGHT
					const isVerde = name === 'cajero_verde'
					const textureKey = isVerde
						? CAJERO_VERDE_SPRITESHEET_KEY
						: CAJERO_AMARILLO_SPRITESHEET_KEY
					const defaultSpeed = isVerde
						? CAJERO_VERDE_DEFAULT_MOVE_SPEED
						: CAJERO_AMARILLO_DEFAULT_MOVE_SPEED

					if (this.enemiesDecorativeMode)
					{
						const cajeroDecorative = this.matter.add.sprite(
							x + (enemyWidth * 0.5),
							y + (enemyHeight * 0.5),
							textureKey,
							0
						)
							.setDisplaySize(enemyWidth, enemyHeight)
							.setDepth(GAMEPLAY_RENDER_DEPTH)
						const cajeroCollider = this.resolveEnemyColliderConfig(objData, this.cajeroColliderDefaults)
						this.applyEnemyCollider(cajeroDecorative, cajeroCollider)
						this.setupDecorativeEnemyBody(cajeroDecorative)
						break
					}

					const cajero = this.matter.add.sprite(
						x + (enemyWidth * 0.5),
						y + (enemyHeight * 0.5),
						textureKey,
						0
					)
						.setDisplaySize(enemyWidth, enemyHeight)
						.setDepth(GAMEPLAY_RENDER_DEPTH)
					const cajeroCollider = this.resolveEnemyColliderConfig(objData, this.cajeroColliderDefaults)
					this.applyEnemyCollider(cajero, cajeroCollider)
					;(cajero.body as MatterJS.BodyType).collisionFilter.group = CAJERO_NO_SELF_COLLISION_GROUP
					cajero.setFixedRotation()
					cajero.setIgnoreGravity(true)
					cajero.setData('enemyHitBudgetLoss', isVerde ? CAJERO_VERDE_ENEMY_HIT_BUDGET_LOSS : CAJERO_AMARILLO_ENEMY_HIT_BUDGET_LOSS)

					const travelDistance = this.resolveRandomTravelDistance(
						objData,
						CAJERO_DEFAULT_TRAVEL_DISTANCE_MIN,
						CAJERO_DEFAULT_TRAVEL_DISTANCE_MAX
					)
					const moveSpeed = this.readObjectNumberProperties(objData, ['speed', 'velocidad'])
						?? defaultSpeed

					this.cajeros.push(new CajeroController(this, cajero, {
						startX: cajero.x,
						travelDistance,
						moveSpeed,
						animationKeyPrefix: textureKey,
						initialDirection: Math.random() < 0.5 ? -1 : 1
					}, this.obstacles))

					this.obstacles.add('cajero', cajero.body as MatterJS.BodyType)
					break
				}

				case 'health':
				{
					const health = this.matter.add.sprite(x, y, 'health', undefined, {
						isStatic: true,
						isSensor: true
					})
					health.setDepth(GAMEPLAY_RENDER_DEPTH)

					health.setData('type', 'health')
					health.setData('healthPoints', 10)
					break
				}

				case 'spikes':
				{
					const spike = this.matter.add.rectangle(x + (width * 0.5), y + (height * 0.5), width, height, {
						isStatic: true
					})
					this.obstacles.add('spikes', spike)
					break
				}

				case 'moneda':
				{
					const coinReward = this.resolveCoinReward(objData)
					const coinWidth = width > 0 ? width : COIN_FRAME_WIDTH
					const coinHeight = height > 0 ? height : COIN_FRAME_HEIGHT
					const coin = this.matter.add.sprite(x + (coinWidth * 0.5), y + (coinHeight * 0.5), COIN_SPRITESHEET_KEY, 0, {
						isStatic: true,
						isSensor: true
					})
					coin.setDisplaySize(coinWidth, coinHeight)
					coin.setDepth(GAMEPLAY_RENDER_DEPTH)
					coin.setData('type', 'coin')
					coin.setData('budgetValue', coinReward.budgetPoints)
					coin.setData('coinRewardType', coinReward.coinType)
					coin.play('coin-spin')
					break
				}

				case 'potenciador':
				case 'potenciador de medidas':
				{
					this.createCardPowerupPickup(objData)
					break
				}

				case 'nube1':
				case 'nube2':
				{
					const cloudSpec = this.resolveCloudSpec(objData)
					if (cloudSpec)
					{
						pendingClouds.push(cloudSpec)
					}
					break
				}

				case 'BigBen':
				case 'london-bridge':
				case 'bpalace':
				{
					const buildingSpec = this.resolveBuildingSpec(objData, layer.name)
					if (buildingSpec)
					{
						pendingBuildings.push(buildingSpec)
					}
					break
				}

				default:
				{
					if (layer.name === 'Nubes')
					{
						const cloudSpec = this.resolveCloudSpec(objData)
						if (cloudSpec)
						{
							pendingClouds.push(cloudSpec)
						}
					}

					if (layer.name === 'Edificios' || layer.name === 'Mobiliario')
					{
						const buildingSpec = this.resolveBuildingSpec(objData, layer.name)
						if (buildingSpec)
						{
							pendingBuildings.push(buildingSpec)
						}
					}

					if (layer.name === 'Bloques')
					{
						const buildingSpec = this.resolveBuildingSpec(objData, layer.name)
						if (buildingSpec)
						{
							pendingBuildings.push(buildingSpec)
						}
					}
					break
				}
			}
			})
		})

		this.ensureVisualTextures([...pendingBuildings, ...pendingClouds], () => {
			pendingClouds.forEach(cloudSpec => {
				this.createMovingCloud(cloudSpec)
			})

			pendingBuildings.forEach(buildingSpec => {
				this.createStaticBuilding(buildingSpec)
			})
		})

	}

	destroy()
	{
		setPersistentPlayTimeState({ elapsedMs: this.elapsedMapTimeMs })
		this.levelThreeFinalLockActive = false
		this.levelThreeFinalCameraLocked = false
		this.levelThreeFinalCameraScrollX = 0
		this.levelThreeFinalCameraScrollY = 0
		if (this.levelThreeFinalCameraLockTween)
		{
			this.levelThreeFinalCameraLockTween.stop()
			this.levelThreeFinalCameraLockTween = undefined
		}
		this.finalBossMusicTriggered = false
		if (this.levelMusic)
		{
			this.levelMusic.stop()
			this.levelMusic.destroy()
			this.levelMusic = undefined
		}
		this.disableInactivityWatchdog()
		this.unbindInactivityInput()
		this.unbindRankingEventForwarders()
		this.destroyFinalBoss(false)
		this.destroyInactivityModal()
		if (this.levelExitTween)
		{
			this.levelExitTween.stop()
			this.levelExitTween = undefined
		}
		this.scene.stop('ui')
		if (this.londonResizeHandler)
		{
			this.scale.off(Phaser.Scale.Events.RESIZE, this.londonResizeHandler)
			this.londonResizeHandler = undefined
		}
		this.londonBackground = undefined
		this.tpvVoladores.forEach(tpv => tpv.destroy())
		this.cajeros.forEach(cajero => cajero.destroy())
		this.movingClouds.forEach(cloud => cloud.sprite.destroy())
		this.movingClouds = []
		this.movingBuildings = []
		this.lastStableGroundY = undefined
		this.currentMapTileHeightPx = 0
		this.staticBuildings.forEach(building => building.destroy())
		this.staticBuildings = []
	}

	update(t: number, dt: number)
	{
		this.updateElapsedMapTime(dt)
		this.updateLevelCountdown(dt)

		if (this.hasContinuousActivityInput())
		{
			this.trackActivity()
		}

		if (this.londonBackground)
		{
			this.londonBackground.tilePositionX = this.cameras.main.scrollX * this.londonParallaxFactorX
			this.londonBackground.tilePositionY = this.cameras.main.scrollY * this.londonParallaxFactorY
		}

		this.updateLevelThreeFinalEncounterState()
		this.enforceLevelThreeFinalCameraLock()
		this.updateFinalBossMovement(dt)

		if (this.shouldStartLevelOneTransition())
		{
			this.startLevelExitSequence()
		}

		if (!this.levelExitSequenceActive)
		{
			this.playerController?.update(dt)
			this.captureStableGroundY()
		}

		this.enforceLevelThreeVisibleBoundsLock()

		this.tpvVoladores.forEach(tpv => tpv.update(dt))
		this.cajeros.forEach(cajero => cajero.update(dt))
	}

	private captureStableGroundY()
	{
		if (!this.penquin)
		{
			return
		}

		const body = this.penquin.body as MatterJS.BodyType | undefined
		if (!body || body.isSensor)
		{
			return
		}

		if (Math.abs(body.velocity.y) > LEVEL_END_STABLE_VERTICAL_SPEED_MAX)
		{
			return
		}

		this.lastStableGroundY = body.position.y
	}

	private updateElapsedMapTime(dt: number)
	{
		if (!Number.isFinite(dt) || dt <= 0)
		{
			return
		}

		this.elapsedMapTimeMs += dt
		const currentSecond = Math.floor(this.elapsedMapTimeMs / 1000)
		if (currentSecond === this.elapsedMapTimeLastSecond)
		{
			return
		}

		this.elapsedMapTimeLastSecond = currentSecond
		this.emitPlayTimeChanged()
	}

	private emitPlayTimeChanged()
	{
		setPersistentPlayTimeState({ elapsedMs: this.elapsedMapTimeMs })
		events.emit('playtime-changed', {
			elapsedMs: this.elapsedMapTimeMs
		})
	}

	private emitLevelTimeChanged()
	{
		const payload: LevelTimeChangedPayload = {
			remainingMs: Math.max(0, Math.floor(this.levelTimeRemainingMs)),
			visible: this.levelTimeVisible
		}
		events.emit('level-time-changed', payload)
	}

	private disableLevelCountdown(hideHud: boolean)
	{
		this.levelTimeActive = false
		this.levelCriticalMusicActive = false
		this.applyLevelMusicPlaybackRate(LEVEL_BACKGROUND_MUSIC_RATE_NORMAL)
		if (hideHud)
		{
			this.levelTimeVisible = false
		}
		this.emitLevelTimeChanged()
	}

	private updateLevelCountdown(dt: number)
	{
		if (!this.levelTimeActive)
		{
			return
		}

		if (this.levelExitSequenceActive || this.levelTimeExpired)
		{
			return
		}

		if (!Number.isFinite(dt) || dt <= 0)
		{
			return
		}

		this.levelTimeRemainingMs = Math.max(0, this.levelTimeRemainingMs - dt)
		const remainingSeconds = Math.floor(this.levelTimeRemainingMs / 1000)
		if (remainingSeconds !== this.levelTimeLastSecond)
		{
			this.levelTimeLastSecond = remainingSeconds
			this.syncLevelCriticalMusicState(remainingSeconds)
			this.emitLevelTimeChanged()
		}

		if (this.levelTimeRemainingMs > 0)
		{
			return
		}

		this.levelTimeExpired = true
		this.levelTimeActive = false
		this.handleLevelTimeoutGameOver()
	}

	private handleLevelTimeoutGameOver()
	{
		if (this.levelExitSequenceActive)
		{
			return
		}

		this.levelExitSequenceActive = true
		this.disableLevelCountdown(false)
		this.disableInactivityWatchdog()
		this.unbindInactivityInput()

		const budgetState = this.playerController?.getBudgetState()
		const nicknameFromRegistry = this.game.registry.get('playerNickname')
		const nickname = typeof nicknameFromRegistry === 'string' && nicknameFromRegistry.trim().length > 0
			? nicknameFromRegistry.trim().toUpperCase()
			: DEFAULT_NICKNAME
		const finalBudget = 0
		const finalMaxBudget = Math.max(0, Math.round(budgetState?.max ?? 3000))
		const elapsedMs = Math.max(0, Math.floor(this.elapsedMapTimeMs))

		setPersistentGameOverSummary({
			nickname,
			finalBudget,
			finalMaxBudget,
			elapsedMs
		})

		clearPersistentBudgetState()
		clearPersistentPlayTimeState()

		this.scene.start(DAILY_RANKING_SCENE_KEY, {
			summary: {
				nickname,
				finalBudget,
				finalMaxBudget,
				elapsedMs
			},
			endReason: 'game_over'
		})
	}

	private bindRankingEventForwarders()
	{
		events.on('coin-collected', this.onCoinCollected, this)
		events.on('enemy-hit', this.onEnemyHit, this)
		events.on('spike-hit', this.onSpikeHit, this)
		events.on('health-picked', this.onHealthPicked, this)
		events.on('tpv-volador-stomped', this.onTpvStomped, this)
		events.on('cajero-stomped', this.onCajeroStomped, this)
		events.on('final-boss-card-hit', this.onFinalBossCardHit, this)
	}

	private unbindRankingEventForwarders()
	{
		events.off('coin-collected', this.onCoinCollected, this)
		events.off('enemy-hit', this.onEnemyHit, this)
		events.off('spike-hit', this.onSpikeHit, this)
		events.off('health-picked', this.onHealthPicked, this)
		events.off('tpv-volador-stomped', this.onTpvStomped, this)
		events.off('cajero-stomped', this.onCajeroStomped, this)
		events.off('final-boss-card-hit', this.onFinalBossCardHit, this)
	}

	private onCoinCollected(payload: CoinCollectedPayload)
	{
		void this.sendRunEvent('coin_collected', {
			amount: Math.max(0, Math.round(payload.amount)),
			reward_type: payload.rewardType
		})
	}

	private onEnemyHit(payload: EnemyHitPayload)
	{
		void this.sendRunEvent('enemy_hit', {
			amount: Math.max(0, Math.round(payload.amount)),
			enemy_type: payload.enemyType
		})
	}

	private onSpikeHit(payload: SpikeHitPayload)
	{
		void this.sendRunEvent('spike_hit', {
			amount: Math.max(0, Math.round(payload.amount))
		})
	}

	private onHealthPicked(payload: HealthPickedPayload)
	{
		void this.sendRunEvent('health_pickup', {
			amount: Math.max(0, Math.round(payload.amount))
		})
	}

	private onTpvStomped()
	{
		void this.sendRunEvent('enemy_stomp', {
			enemy_type: 'tpv_volador'
		})
	}

	private onCajeroStomped()
	{
		void this.sendRunEvent('enemy_stomp', {
			enemy_type: 'cajero'
		})
	}

	private emitFinalBossHealthChanged(visible: boolean)
	{
		events.emit('final-boss-health-changed', {
			visible,
			current: this.finalBossHealthPercent,
			max: FINAL_BOSS_MAX_HEALTH_PERCENT
		})
	}

	private onFinalBossCardHit(payload: FinalBossCardHitPayload)
	{
		if (!this.levelThreeFinalLockActive || !this.finalBoss || !this.finalBoss.active)
		{
			return
		}

		if (this.finalBossDefeated || this.finalBossRetreating)
		{
			return
		}

		if (this.time.now < this.finalBossHitRecoverUntilMs)
		{
			return
		}

		if (typeof payload?.targetBodyId === 'number')
		{
			const body = this.finalBoss.body as MatterJS.BodyType | undefined
			if (!body || body.id !== payload.targetBodyId)
			{
				return
			}
		}

		this.finalBossHealthPercent = Phaser.Math.Clamp(
			this.finalBossHealthPercent - FINAL_BOSS_CARD_HIT_DAMAGE_PERCENT,
			0,
			FINAL_BOSS_MAX_HEALTH_PERCENT
		)
		this.startFinalBossDamageImmunity(FINAL_BOSS_DAMAGE_IMMUNITY_MS)
		this.emitFinalBossHealthChanged(true)
		this.finalBossDirection = this.getFinalBossValidMoveDirection(this.finalBossMoveDirection)

		if (this.finalBossDirection < 0)
		{
			this.finalBoss.play(FINAL_BOSS_ANIM_HIT_LEFT, true)
		}
		else
		{
			this.finalBoss.play(FINAL_BOSS_ANIM_HIT_RIGHT, true)
		}

		if (this.finalBossHealthPercent <= 0)
		{
			this.completeFinalBossDefeatSequence()
			return
		}
	}

	private startFinalBossDamageImmunity(durationMs: number)
	{
		if (!this.finalBoss || !this.finalBoss.active)
		{
			return
		}

		const boss = this.finalBoss
		const safeDurationMs = Math.max(1, Math.round(durationMs))
		this.finalBossHitRecoverUntilMs = this.time.now + safeDurationMs
		const immunitySequence = ++this.finalBossDamageImmunitySequence

		boss.setData('damageImmune', true)
		boss.setAlpha(1)

		if (this.finalBossDamageImmunityTween)
		{
			this.finalBossDamageImmunityTween.stop()
			this.finalBossDamageImmunityTween = undefined
		}

		const blinkDuration = Math.max(30, FINAL_BOSS_DAMAGE_IMMUNITY_BLINK_DURATION_MS)
		const blinkCycles = Math.max(1, Math.floor(safeDurationMs / (blinkDuration * 2)))

		this.finalBossDamageImmunityTween = this.tweens.add({
			targets: boss,
			alpha: 0.6,
			duration: blinkDuration,
			ease: 'Linear',
			yoyo: true,
			repeat: blinkCycles - 1,
			onComplete: () => {
				if (this.finalBossDamageImmunitySequence !== immunitySequence)
				{
					return
				}

				this.finalBossDamageImmunityTween = undefined
				if (boss.active)
				{
					boss.setAlpha(1)
				}
			}
		})

		this.time.delayedCall(safeDurationMs, () => {
			if (this.finalBossDamageImmunitySequence !== immunitySequence)
			{
				return
			}

			if (!boss.active)
			{
				return
			}

			if (this.finalBossDamageImmunityTween)
			{
				this.finalBossDamageImmunityTween.stop()
				this.finalBossDamageImmunityTween = undefined
			}

			boss.setData('damageImmune', false)
			boss.setAlpha(1)
		})
	}

	private async syncOfflineRankingQueue()
	{
		if (!this.rankingApi)
		{
			return
		}

		try
		{
			const resolvedSessions = await flushQueuedOperations(this.rankingApi)
			if (resolvedSessions.length === 0)
			{
				return
			}

			const activeSession = getPersistentRankingRunSession()
			const match = activeSession && resolvedSessions.find(resolved => resolved.localSessionId === activeSession.runId)
			if (activeSession && match)
			{
				setPersistentRankingRunSession({
					...activeSession,
					runId: match.runId,
					runToken: match.runToken
				})
			}
		}
		catch (error)
		{
			// console.warn('[ranking-api] offline queue sync failed', error)
		}
	}

	private async ensureRunSessionStarted()
	{
		if (!this.rankingApi)
		{
			return
		}

		const existingSession = getPersistentRankingRunSession()
		if (existingSession && !existingSession.finished)
		{
			return
		}

		if (this.pendingRunStartPromise)
		{
			return this.pendingRunStartPromise
		}

		this.pendingRunStartPromise = (async () => {
			const nicknameFromRegistry = this.game.registry.get('playerNickname')
			const nickname = typeof nicknameFromRegistry === 'string' && nicknameFromRegistry.trim().length > 0
				? nicknameFromRegistry.trim().toUpperCase()
				: DEFAULT_NICKNAME

			const clientStartedAtMs = Date.now()
			const startPayload = {
				nickname,
				level_index: this.currentLevelIndex,
				map_key: this.currentLevel.mapKey,
				build_version: 'game-client-v1',
				client_started_at_ms: clientStartedAtMs
			}

			const isAvailable = await checkConnectivityAndApiAvailability(this.rankingApi!)
			if (!isAvailable)
			{
				const localSessionId = createLocalSessionId()
				enqueueStartOperation(localSessionId, startPayload)
				setPersistentRankingRunSession({
					runId: localSessionId,
					runToken: '',
					startedAtClientMs: clientStartedAtMs,
					seq: 0,
					finished: false,
					levelIndex: this.currentLevelIndex,
					mapKey: this.currentLevel.mapKey
				})
				this.pendingRunStartPromise = undefined
				return
			}

			try
			{
				const response = await this.rankingApi!.startRun(startPayload)

				setPersistentRankingRunSession({
					runId: response.run_id,
					runToken: response.run_token,
					startedAtClientMs: clientStartedAtMs,
					seq: 0,
					finished: false,
					levelIndex: this.currentLevelIndex,
					mapKey: this.currentLevel.mapKey
				})
			}
			catch (error)
			{
				// console.warn('[ranking-api] start run failed', error)
			}
			finally
			{
				this.pendingRunStartPromise = undefined
			}
		})()

		return this.pendingRunStartPromise
	}

	private async sendRunEvent(eventType: string, payload: Record<string, unknown>)
	{
		if (!this.rankingApi)
		{
			return
		}

		let session = getPersistentRankingRunSession()
		if (!session)
		{
			await this.ensureRunSessionStarted()
			session = getPersistentRankingRunSession()
		}

		if (!session || session.finished)
		{
			return
		}

		const nextSeq = session.seq + 1
		setPersistentRankingRunSession({
			...session,
			seq: nextSeq
		})

		const eventTimeMs = Math.max(0, Date.now() - session.startedAtClientMs)
		const eventPayload = {
			seq: nextSeq,
			event_type: eventType,
			event_time_ms: eventTimeMs,
			payload,
			idempotency_key: `${session.runId}-${nextSeq}-${eventType}`
		}

		if (isLocalSessionId(session.runId))
		{
			enqueueEventOperation(session.runId, eventPayload)
			return
		}

		try
		{
			await this.rankingApi.sendEvent({ ...eventPayload, run_id: session.runId }, session.runToken)
		}
		catch (error)
		{
			// console.warn('[ranking-api] send event failed, queuing for later sync', { eventType, error })
			enqueueEventOperation(session.runId, eventPayload, session.runToken)
		}
	}

	private shouldStartLevelOneTransition(): boolean
	{
		if (this.currentLevelIndex === LEVEL_THREE_INDEX)
		{
			return false
		}

		if (this.levelExitSequenceActive)
		{
			return false
		}

		if (!this.penquin)
		{
			return false
		}

		if (typeof this.levelEndTriggerX !== 'number' || !Number.isFinite(this.levelEndTriggerX))
		{
			return false
		}

		return this.penquin.x >= this.levelEndTriggerX && this.isPenquinOnGroundForLevelEnd()
	}

	private isPenquinOnGroundForLevelEnd(): boolean
	{
		if (!this.penquin)
		{
			return false
		}

		const body = this.penquin.body as MatterJS.BodyType | undefined
		if (!body || body.isSensor)
		{
			return false
		}

		if (Math.abs(body.velocity.y) > LEVEL_END_STABLE_VERTICAL_SPEED_MAX)
		{
			return false
		}

		const surfaceY = this.findNearestStaticSurfaceYAtX(
			body.position.x,
			body.bounds.max.y - 2,
			body.bounds.max.y + LEVEL_END_FINISH_GROUND_TOLERANCE_PX
		)
		if (surfaceY === undefined)
		{
			return false
		}

		return surfaceY >= (body.bounds.max.y - 2)
	}

	private setupLevelThreeFinalBlocker(map: Phaser.Tilemaps.Tilemap)
	{
		void map
	}

	private updateLevelThreeFinalEncounterState()
	{
		if (this.currentLevelIndex !== LEVEL_THREE_INDEX)
		{
			return
		}

		if (!this.penquin)
		{
			return
		}

		if (this.levelThreeFinalLockActive)
		{
			return
		}

		if (!this.hasLevelThreeReachedCameraEnd())
		{
			return
		}

		this.levelThreeFinalLockActive = true
		this.disableLevelCountdown(true)
		this.lockLevelThreeFinalCamera()
		this.startFinalBossMusic()
		this.spawnFinalBoss()
	}

	private spawnFinalBoss()
	{
		if (this.finalBoss || !this.penquin)
		{
			return
		}

		const worldView = this.cameras.main.worldView
		const fallbackSpawnX = this.penquin.x
		const fallbackEntryTargetY = this.penquin.y
		const tileCenterSpawnX = this.currentMapTileWidthPx > 0
			? ((FINAL_BOSS_ENTRY_TARGET_TILE_X + 0.5) * this.currentMapTileWidthPx)
			: fallbackSpawnX
		const rawSpawnX = tileCenterSpawnX
		const rawSpawnY = worldView.top - FINAL_BOSS_ENTRY_OFFSET_Y
		const boss = this.matter.add.sprite(rawSpawnX, rawSpawnY, FINAL_BOSS_SPRITESHEET_KEY, 0) as Phaser.Physics.Matter.Sprite
		boss.setDepth(FINAL_BOSS_RENDER_DEPTH)

		this.applyEnemyCollider(boss, FINAL_BOSS_COLLIDER)
		const bossBody = boss.body as MatterJS.BodyType | undefined
		const groundSurfaceY = bossBody
			? this.findNearestStaticSurfaceYAtX(rawSpawnX, worldView.top, this.currentMapHeightPx)
			: undefined
		const entryTargetY = bossBody && groundSurfaceY !== undefined
			? this.resolveBodyCenterYForGroundSurface(bossBody, groundSurfaceY)
			: fallbackEntryTargetY
		// setExistingBody reemplaza el cuerpo, así que estas flags deben aplicarse después.
		boss.setIgnoreGravity(true)
		boss.setSensor(false)
		boss.setFixedRotation()
		boss.setRotation(0)
		boss.setAngularVelocity(0)
		boss.setData('isFinalBoss', true)
		boss.setData('damageImmune', false)
		boss.setData('enemyHitBudgetLoss', FINAL_BOSS_CONTACT_BUDGET_LOSS)
		this.obstacles.add('final-boss', boss.body as MatterJS.BodyType)

		boss.on(Phaser.Animations.Events.ANIMATION_COMPLETE, (animation: Phaser.Animations.Animation) => {
			if (animation.key !== FINAL_BOSS_ANIM_HIT_LEFT && animation.key !== FINAL_BOSS_ANIM_HIT_RIGHT)
			{
				return
			}

			if (!this.finalBoss || !this.finalBoss.active)
			{
				return
			}

			if (this.isFinalBossEntryPauseActive())
			{
				this.finalBoss.anims.stop()
				this.finalBoss.setFrame(FINAL_BOSS_ENTRY_IDLE_FRAME_INDEX)
				return
			}

			this.startFinalBossTravelSegment()
			this.syncFinalBossFacingToPlayer()
		})

		boss.setOnCollide((data: MatterJS.ICollisionPair) => {
			if (!this.finalBoss || !this.finalBoss.active)
			{
				return
			}

			if (this.finalBossEntering || this.isFinalBossEntryPauseActive() || this.finalBossRetreating || this.finalBossDefeated)
			{
				return
			}

			const ownBody = this.finalBoss.body as MatterJS.BodyType | undefined
			if (!ownBody)
			{
				return
			}

			const otherBody = (data.bodyA === ownBody ? data.bodyB : data.bodyA) as MatterJS.BodyType | undefined
			if (!otherBody || otherBody.isSensor)
			{
				return
			}

			const maybeGroundTileBody = otherBody as MatterJS.BodyType & { hasBottomGroundOffset?: boolean }
			if (typeof maybeGroundTileBody.hasBottomGroundOffset === 'boolean')
			{
				// console.log('collision ignored: ground tile body', {
				// 	otherBodyId: otherBody.id,
				// 	hasBottomGroundOffset: maybeGroundTileBody.hasBottomGroundOffset
				// })
				return
			}

			const playerBody = this.penquin?.body as MatterJS.BodyType | undefined
			const collidesWithPlayer = Boolean(playerBody && otherBody === playerBody)

			const horizontalDeltaPx = otherBody.position.x - ownBody.position.x
			if (Math.abs(horizontalDeltaPx) < FINAL_BOSS_COLLISION_HORIZONTAL_ALIGNMENT_MIN_PX)
			{
				return
			}

			if (this.finalBossMoveDirection > 0 && horizontalDeltaPx < 0)
			{
				return
			}

			if (this.finalBossMoveDirection < 0 && horizontalDeltaPx > 0)
			{
				return
			}

			const segmentProgressPx = Math.abs(this.finalBoss.x - this.finalBossSegmentStartX)
			if (segmentProgressPx < FINAL_BOSS_COLLISION_REVERSE_MIN_PROGRESS_PX)
			{
				// console.log('collision ignored due to low segment progress:', {
				// 	horizontalDeltaPx,
				// 	segmentProgressPx,
				// 	minProgressPx: FINAL_BOSS_COLLISION_REVERSE_MIN_PROGRESS_PX,
				// 	collidesWithPlayer
				// })
				return
			}

			// console.log('collision accepted for reverse:', {
			// 	horizontalDeltaPx,
			// 	segmentProgressPx,
			// 	direction: this.finalBossMoveDirection,
			// 	otherBodyId: otherBody.id,
			// 	collidesWithPlayer
			// })

			this.reverseFinalBossDirection('collision', false)
		})

		this.finalBoss = boss
		this.finalBossHealthPercent = FINAL_BOSS_MAX_HEALTH_PERCENT
		this.finalBossDirection = -1
		this.finalBossMoveDirection = -1
		this.finalBossEntryPauseUntilMs = 0
		this.finalBossTargetX = 0
		this.finalBossCurrentSpeedPxPerMs = FINAL_BOSS_MOVE_SPEED_MIN_PX_PER_MS
		this.finalBossHitRecoverUntilMs = 0
		this.finalBossDirectionChangeLockedUntilMs = 0
		this.finalBossRetreating = false
		this.finalBossDefeated = false
		this.finalBossLockedY = entryTargetY

		const halfWidth = Math.max(1, boss.displayWidth * 0.5)
		const mapMaxCenterX = Math.max(halfWidth, this.currentMapWidthPx - halfWidth - 1)
		const spawnX = Phaser.Math.Clamp(rawSpawnX, halfWidth, mapMaxCenterX)
		this.finalBossSegmentStartX = spawnX
		this.finalBossPreviousX = spawnX
		boss.setX(spawnX)
		const bodyApi = this.resolveMatterBodyApi()
		if (bossBody)
		{
			bodyApi.setPosition(bossBody, {
				x: spawnX,
				y: bossBody.position.y
			})
		}

		this.finalBossEntryStartX = spawnX
		this.finalBossEntryStartY = rawSpawnY
		this.finalBossEntryTargetY = entryTargetY
		this.finalBossRetreatTargetX = mapMaxCenterX
		const tileWidth = this.currentMapTileWidthPx > 0 ? this.currentMapTileWidthPx : 1
		const worldLeft = this.cameras.main.worldView.left
		const worldRight = this.cameras.main.worldView.right
		this.finalBossPatrolMinX = Phaser.Math.Clamp(worldLeft + halfWidth, halfWidth, mapMaxCenterX)
		this.finalBossPatrolMaxX = Phaser.Math.Clamp(worldRight - halfWidth, this.finalBossPatrolMinX, mapMaxCenterX)
		if (this.finalBossPatrolMaxX <= this.finalBossPatrolMinX)
		{
			this.finalBossPatrolMinX = halfWidth
			this.finalBossPatrolMaxX = mapMaxCenterX
		}
		const collisionMarginPx = tileWidth * FINAL_BOSS_COLLISION_MARGIN_TILES
		if ((this.finalBossPatrolMaxX - this.finalBossPatrolMinX) > (collisionMarginPx * 2))
		{
			this.finalBossPatrolMinX += collisionMarginPx
			this.finalBossPatrolMaxX -= collisionMarginPx
		}
		if ((this.finalBossPatrolMaxX - this.finalBossPatrolMinX) < FINAL_BOSS_MIN_PATROL_SPAN_PX)
		{
			const halfSpan = FINAL_BOSS_MIN_PATROL_SPAN_PX * 0.5
			this.finalBossPatrolMinX = Phaser.Math.Clamp(spawnX - halfSpan, halfWidth, mapMaxCenterX)
			this.finalBossPatrolMaxX = Phaser.Math.Clamp(spawnX + halfSpan, this.finalBossPatrolMinX, mapMaxCenterX)
		}
		if (this.finalBossPatrolMaxX <= this.finalBossPatrolMinX)
		{
			this.finalBossPatrolMaxX = Math.min(mapMaxCenterX, this.finalBossPatrolMinX + 1)
		}
		this.finalBossEntryTargetX = spawnX
		this.finalBossEntering = true

		boss.play(FINAL_BOSS_ANIM_WALK_LEFT, true)
		this.startFinalBossEntryTween()
		this.logFinalBossDebug('spawn')
		this.emitFinalBossHealthChanged(true)
	}

	private startFinalBossEntryTween()
	{
		if (!this.finalBoss)
		{
			return
		}

		const body = this.finalBoss.body as MatterJS.BodyType | undefined
		if (!body)
		{
			return
		}

		if (this.finalBossEntryTween)
		{
			this.finalBossEntryTween.stop()
			this.finalBossEntryTween = undefined
		}

		const startY = this.finalBoss.y
		const targetY = this.finalBossEntryTargetY
		const distance = Math.abs(startY - targetY)
		const duration = Math.max(250, Math.round(distance / FINAL_BOSS_ENTRY_MOVE_SPEED_PX_PER_MS))
		this.logFinalBossDebug('entry-tween-start')

		this.finalBossEntryTween = this.tweens.addCounter({
			from: startY,
			to: targetY,
			duration,
			ease: 'Linear',
			onUpdate: tween => {
				if (!this.finalBoss || !this.finalBoss.active)
				{
					return
				}

				const y = tween.getValue()
				this.finalBoss.setY(y)
				this.resolveMatterBodyApi().setPosition(body, {
					x: body.position.x,
					y
				})
			},
			onComplete: () => {
				this.finalBossEntryTween = undefined
				if (!this.finalBoss || !this.finalBoss.active || this.finalBossRetreating)
				{
					return
				}

				this.beginFinalBossEntryPause()
				this.logFinalBossDebug('entry-tween-complete')
			}
		})
	}

	private isFinalBossEntryPauseActive(): boolean
	{
		return this.finalBossEntryPauseUntilMs > this.time.now
	}

	private beginFinalBossEntryPause()
	{
		if (!this.finalBoss || !this.finalBoss.active)
		{
			return
		}

		this.finalBossEntering = false
		this.finalBossDirection = -1
		this.finalBossEntryPauseUntilMs = this.time.now + FINAL_BOSS_ENTRY_PAUSE_DURATION_MS
		this.finalBossLockedY = this.finalBoss.y
		this.finalBossTargetX = this.finalBoss.x
		if (this.finalBossJumpTween)
		{
			this.finalBossJumpTween.stop()
			this.finalBossJumpTween = undefined
		}
		this.finalBossJumpOffsetPx = 0
		this.finalBoss.anims.stop()
		this.finalBoss.setFrame(FINAL_BOSS_ENTRY_IDLE_FRAME_INDEX)
	}

	private isFinalBossHitAnimationActive(): boolean
	{
		if (!this.finalBoss?.anims.currentAnim)
		{
			return false
		}

		const currentAnimKey = this.finalBoss.anims.currentAnim.key
		if (currentAnimKey !== FINAL_BOSS_ANIM_HIT_LEFT && currentAnimKey !== FINAL_BOSS_ANIM_HIT_RIGHT)
		{
			return false
		}

		return this.finalBoss.anims.isPlaying
	}

	private getFinalBossValidMoveDirection(preferredDirection: -1 | 1): -1 | 1
	{
		if (!this.finalBoss)
		{
			return preferredDirection
		}

		const edgeSnapTolerancePx = 1
		if (this.finalBoss.x <= (this.finalBossPatrolMinX + edgeSnapTolerancePx))
		{
			return 1
		}

		if (this.finalBoss.x >= (this.finalBossPatrolMaxX - edgeSnapTolerancePx))
		{
			return -1
		}

		return preferredDirection
	}

	private syncFinalBossFacingToPlayer()
	{
		if (!this.finalBoss || !this.finalBoss.active)
		{
			return
		}

		this.finalBossDirection = this.getFinalBossValidMoveDirection(this.finalBossMoveDirection)

		if (this.finalBossEntering || this.isFinalBossEntryPauseActive())
		{
			return
		}

		if (this.isFinalBossHitAnimationActive())
		{
			return
		}

		if (this.finalBossDirection < 0)
		{
			this.finalBoss.play(FINAL_BOSS_ANIM_WALK_LEFT, true)
		}
		else
		{
			this.finalBoss.play(FINAL_BOSS_ANIM_WALK_RIGHT, true)
		}
	}

	private ensureFinalBossContinuousJump()
	{
		if (!this.finalBoss || !this.finalBoss.active)
		{
			return
		}

		if (this.finalBossEntering || this.isFinalBossEntryPauseActive() || this.finalBossRetreating || this.finalBossDefeated)
		{
			return
		}

		if (this.isFinalBossHitAnimationActive())
		{
			return
		}

		if (this.finalBossJumpTween && this.finalBossJumpTween.isPlaying())
		{
			return
		}

		const jumpHeightPx = this.getFinalBossJumpHeightPx()
		if (jumpHeightPx <= 0)
		{
			return
		}

		const durationMs = Math.max(150, Math.round(FINAL_BOSS_JUMP_DURATION_MS))

		if (this.finalBossJumpTween)
		{
			this.finalBossJumpTween.stop()
			this.finalBossJumpTween = undefined
		}

		this.finalBossJumpOffsetPx = 0
		this.finalBossJumpTween = this.tweens.addCounter({
			from: 0,
			to: 1,
			duration: durationMs,
			ease: 'Sine.easeInOut',
			yoyo: true,
			repeat: -1,
			onUpdate: tween => {
				const progress = Phaser.Math.Clamp(tween.getValue(), 0, 1)
				this.finalBossJumpOffsetPx = progress * jumpHeightPx
			}
		})
	}

	private stopFinalBossJump()
	{
		if (this.finalBossJumpTween)
		{
			this.finalBossJumpTween.stop()
			this.finalBossJumpTween = undefined
		}

		this.finalBossJumpOffsetPx = 0
	}

	private startFinalBossTravelSegment()
	{
		if (!this.finalBoss)
		{
			return
		}
		const tileWidth = FINAL_BOSS_TRAVEL_TILE_SIZE_PX
		// console.log('tileWidth:', tileWidth)
		const visibleTiles = Math.max(1, Math.round(this.cameras.main.width / tileWidth))
		// console.log('visibleTiles:', visibleTiles)
		const maxTravelTiles = Math.max(FINAL_BOSS_MIN_TRAVEL_TILES, visibleTiles - (FINAL_BOSS_COLLISION_MARGIN_TILES * 2))
		// console.log('maxTravelTiles:', maxTravelTiles)
		const travelTiles = Phaser.Math.Between(FINAL_BOSS_MIN_TRAVEL_TILES, maxTravelTiles)
		// console.log('travelTiles:', travelTiles)
		const travelDistancePx = travelTiles * tileWidth
		// console.log('travelDistancePx:', travelDistancePx)
		const segmentDurationMs = Phaser.Math.Between(
			FINAL_BOSS_SEGMENT_DURATION_MIN_MS,
			FINAL_BOSS_SEGMENT_DURATION_MAX_MS
		)
		const computedSpeedPxPerMs = travelDistancePx / Math.max(1, segmentDurationMs)
		this.finalBossCurrentSpeedPxPerMs = Math.min(
			FINAL_BOSS_MOVE_SPEED_MAX_PX_PER_MS,
			Math.max(0, computedSpeedPxPerMs)
		)
		// console.log('segmentDurationMs:', segmentDurationMs)
		// console.log('computedSpeedPxPerMs:', computedSpeedPxPerMs)
		// console.log('appliedSpeedPxPerMs:', this.finalBossCurrentSpeedPxPerMs)

		const currentX = this.finalBoss.x
		const leftRoomPx = Math.max(0, currentX - this.finalBossPatrolMinX)
		const rightRoomPx = Math.max(0, this.finalBossPatrolMaxX - currentX)
		// console.log('patrol range:', this.finalBossPatrolMinX, this.finalBossPatrolMaxX)
		// console.log('currentX:', currentX, 'leftRoomPx:', leftRoomPx, 'rightRoomPx:', rightRoomPx)

		this.finalBossMoveDirection = this.getFinalBossValidMoveDirection(this.finalBossMoveDirection)
		if (this.finalBossMoveDirection > 0 && rightRoomPx < tileWidth)
		{
			this.finalBossMoveDirection = -1
		}
		else if (this.finalBossMoveDirection < 0 && leftRoomPx < tileWidth)
		{
			this.finalBossMoveDirection = 1
		}
		this.finalBossDirection = this.finalBossMoveDirection

		let targetX = currentX + (this.finalBossMoveDirection * travelDistancePx)
		// console.log('targetX before clamp:', targetX)
		targetX = Phaser.Math.Clamp(targetX, this.finalBossPatrolMinX, this.finalBossPatrolMaxX)
		// console.log('targetX after clamp:', targetX)

		if (Math.abs(targetX - currentX) < 1)
		{
			this.finalBossMoveDirection = this.getFinalBossValidMoveDirection(this.finalBossMoveDirection > 0 ? -1 : 1)
			this.finalBossDirection = this.finalBossMoveDirection
			targetX = Phaser.Math.Clamp(
				currentX + (this.finalBossMoveDirection * travelDistancePx),
				this.finalBossPatrolMinX,
				this.finalBossPatrolMaxX
			)
			// console.log('targetX fallback after clamp:', targetX)
		}

		this.finalBossTargetX = targetX
		this.finalBossSegmentStartX = currentX

		if (this.isFinalBossHitAnimationActive())
		{
			return
		}

		this.ensureFinalBossContinuousJump()

		if (this.finalBossDirection < 0)
		{
			this.finalBoss.play(FINAL_BOSS_ANIM_WALK_LEFT, true)
		}
		else
		{
			this.finalBoss.play(FINAL_BOSS_ANIM_WALK_RIGHT, true)
		}
	}

	private reverseFinalBossDirection(reason: 'collision' | 'card-hit' | 'edge', force: boolean)
	{
		void reason

		if (!this.finalBoss || !this.finalBoss.active)
		{
			return
		}

		const now = this.time.now
		if (!force && now < this.finalBossDirectionChangeLockedUntilMs)
		{
			return
		}

		this.finalBossDirectionChangeLockedUntilMs = now + FINAL_BOSS_COLLISION_DIRECTION_CHANGE_COOLDOWN_MS
		this.finalBossMoveDirection = this.getFinalBossValidMoveDirection(this.finalBossMoveDirection > 0 ? -1 : 1)
		this.finalBossDirection = this.finalBossMoveDirection
		this.startFinalBossTravelSegment()
	}

	private enforceFinalBossUpright(body: MatterJS.BodyType)
	{
		if (!this.finalBoss)
		{
			return
		}

		this.finalBoss.setRotation(0)
		this.finalBoss.setAngularVelocity(0)
		Phaser.Physics.Matter.Matter.Body.setAngle(body, 0)
		Phaser.Physics.Matter.Matter.Body.setAngularVelocity(body, 0)
	}

	private getFinalBossJumpHeightPx(): number
	{
		const tileHeight = this.currentMapTileHeightPx > 0
			? this.currentMapTileHeightPx
			: (this.currentMapTileWidthPx > 0 ? this.currentMapTileWidthPx : FINAL_BOSS_TRAVEL_TILE_SIZE_PX)

		return Math.max(1, tileHeight * FINAL_BOSS_JUMP_HEIGHT_TILES)
	}

	private updateFinalBossMovement(dt: number)
	{
		if (!this.finalBoss || !this.finalBoss.active)
		{
			return
		}

		const body = this.finalBoss.body as MatterJS.BodyType | undefined
		if (!body)
		{
			return
		}

		this.enforceFinalBossUpright(body)
		this.syncFinalBossFacingToPlayer()

		const speed = this.finalBossCurrentSpeedPxPerMs * Math.max(0, dt)
		if (speed <= 0)
		{
			this.finalBossPreviousX = this.finalBoss.x
			return
		}

		if (this.finalBossRetreating)
		{
			this.stopFinalBossJump()
			const retreatTargetX = Math.max(this.finalBossEntryStartX, this.finalBossRetreatTargetX)
			const nextX = Math.min(retreatTargetX, this.finalBoss.x + speed)
			this.finalBoss.setX(nextX)
			this.resolveMatterBodyApi().setPosition(body, {
				x: nextX,
				y: body.position.y
			})

			if (nextX >= retreatTargetX)
			{
				this.completeFinalBossDefeatSequence()
			}
			this.finalBossPreviousX = nextX
			return
		}

		if (this.finalBossEntering)
		{
			this.stopFinalBossJump()
			this.logFinalBossDebug('tick')
			const isEntryTweenRunning = Boolean(this.finalBossEntryTween && this.finalBossEntryTween.isPlaying())
			if (!isEntryTweenRunning)
			{
				const entryTargetY = this.finalBossEntryTargetY
				const entrySpeed = FINAL_BOSS_ENTRY_MOVE_SPEED_PX_PER_MS * Math.max(0, dt)
				const nextY = Math.min(entryTargetY, this.finalBoss.y + entrySpeed)
				this.finalBoss.setY(nextY)
				this.resolveMatterBodyApi().setPosition(body, {
					x: body.position.x,
					y: nextY
				})

				if (nextY >= entryTargetY)
				{
					this.beginFinalBossEntryPause()
					this.logFinalBossDebug('entry-fallback-complete')
				}
			}
			this.finalBossPreviousX = this.finalBoss.x
			return
		}

		if (this.isFinalBossEntryPauseActive())
		{
			this.stopFinalBossJump()
			this.finalBoss.setY(this.finalBossLockedY)
			this.resolveMatterBodyApi().setPosition(body, {
				x: body.position.x,
				y: this.finalBossLockedY
			})
			this.finalBoss.setVelocity(0, 0)
			this.finalBoss.anims.stop()
			this.finalBoss.setFrame(FINAL_BOSS_ENTRY_IDLE_FRAME_INDEX)
			this.finalBossPreviousX = this.finalBoss.x
			return
		}

		if (this.finalBossEntryPauseUntilMs > 0)
		{
			this.finalBossEntryPauseUntilMs = 0
			this.startFinalBossTravelSegment()
		}

		this.ensureFinalBossContinuousJump()

		const currentX = this.finalBoss.x
		const finalBossTargetY = this.finalBossLockedY - this.finalBossJumpOffsetPx
		this.finalBoss.setY(finalBossTargetY)
		this.resolveMatterBodyApi().setPosition(body, {
			x: body.position.x,
			y: finalBossTargetY
		})
		this.finalBossMoveDirection = this.getFinalBossValidMoveDirection(this.finalBossMoveDirection)
		this.finalBossDirection = this.finalBossMoveDirection

		if (this.finalBossMoveDirection > 0 && currentX >= this.finalBossPatrolMaxX - 1)
		{
			this.reverseFinalBossDirection('edge', false)
			this.finalBossPreviousX = this.finalBoss.x
			return
		}

		if (this.finalBossMoveDirection < 0 && currentX <= this.finalBossPatrolMinX + 1)
		{
			this.reverseFinalBossDirection('edge', false)
			this.finalBossPreviousX = this.finalBoss.x
			return
		}

		if (!Number.isFinite(this.finalBossTargetX) || Math.abs(this.finalBossTargetX - currentX) < 1)
		{
			this.startFinalBossTravelSegment()
		}

		let nextX = currentX + (this.finalBossMoveDirection * speed)
		let reachedSegmentTarget = false
		if (this.finalBossMoveDirection < 0)
		{
			if (nextX <= this.finalBossTargetX)
			{
				nextX = this.finalBossTargetX
				reachedSegmentTarget = true
			}
		}
		else if (nextX >= this.finalBossTargetX)
		{
			nextX = this.finalBossTargetX
			reachedSegmentTarget = true
		}

		nextX = Phaser.Math.Clamp(nextX, this.finalBossPatrolMinX, this.finalBossPatrolMaxX)
		// console.log('update movement:', {
		// 	dt,
		// 	direction: this.finalBossMoveDirection,
		// 	speed,
		// 	currentX,
		// 	nextX,
		// 	targetX: this.finalBossTargetX
		// })

		this.finalBoss.setX(nextX)
		this.resolveMatterBodyApi().setPosition(body, {
			x: nextX,
			y: body.position.y
		})
		this.finalBossPreviousX = nextX

		if (reachedSegmentTarget)
		{
			this.startFinalBossTravelSegment()
		}
	}

	private startFinalBossRetreat()
	{
		if (!this.finalBoss || !this.finalBoss.active)
		{
			return
		}

		this.finalBossDefeated = true
		this.finalBossEntering = false
		this.finalBossEntryPauseUntilMs = 0
		this.finalBossTargetX = this.finalBoss.x
		this.finalBossHitRecoverUntilMs = 0
		if (this.finalBossDamageImmunityTween)
		{
			this.finalBossDamageImmunityTween.stop()
			this.finalBossDamageImmunityTween = undefined
		}
		this.finalBossDamageImmunitySequence++
		this.finalBossDirectionChangeLockedUntilMs = 0
		if (this.finalBossEntryTween)
		{
			this.finalBossEntryTween.stop()
			this.finalBossEntryTween = undefined
		}
		this.stopFinalBossJump()
		this.finalBossRetreating = true
		this.finalBossDirection = 1
		this.finalBossMoveDirection = 1
		this.finalBoss.play(FINAL_BOSS_ANIM_WALK_RIGHT, true)
	}

	private completeFinalBossDefeatSequence()
	{
		if (this.levelExitSequenceActive)
		{
			return
		}

		this.levelExitSequenceActive = true
		this.pendingLevelStartBudgetState = this.playerController?.getBudgetState()
		if (this.pendingLevelStartBudgetState)
		{
			setPersistentBudgetState(this.pendingLevelStartBudgetState)
		}
		setPersistentPlayTimeState({ elapsedMs: this.elapsedMapTimeMs })
		this.disableInactivityWatchdog()
		this.unbindInactivityInput()
		this.destroyFinalBoss(true)
		this.playTransitionVideoScene({
			targetSceneKey: DAILY_RANKING_SCENE_KEY,
			targetSceneData: this.buildDailyRankingSceneData()
		})
	}

	private destroyFinalBoss(emitHealthHidden: boolean)
	{
		if (this.finalBoss)
		{
			const body = this.finalBoss.body as MatterJS.BodyType | undefined
			if (body)
			{
				this.obstacles.remove('final-boss', body)
			}
			this.finalBoss.destroy()
		}

		this.finalBoss = undefined
		this.finalBossRetreating = false
		this.finalBossDefeated = false
		this.finalBossEntering = false
		this.finalBossEntryPauseUntilMs = 0
		this.finalBossTargetX = 0
		this.finalBossCurrentSpeedPxPerMs = FINAL_BOSS_MOVE_SPEED_MIN_PX_PER_MS
		if (this.finalBossEntryTween)
		{
			this.finalBossEntryTween.stop()
			this.finalBossEntryTween = undefined
		}
		if (this.finalBossDamageImmunityTween)
		{
			this.finalBossDamageImmunityTween.stop()
			this.finalBossDamageImmunityTween = undefined
		}
		this.stopFinalBossJump()
		this.finalBossDamageImmunitySequence++
		this.finalBossDirection = -1
		this.finalBossMoveDirection = -1
		this.finalBossHealthPercent = FINAL_BOSS_MAX_HEALTH_PERCENT
		this.finalBossHitRecoverUntilMs = 0
		this.finalBossDirectionChangeLockedUntilMs = 0
		this.finalBossEntryTargetX = 0
		this.finalBossEntryTargetY = 0
		this.finalBossRetreatTargetX = 0
		this.finalBossLockedY = 0
		this.finalBossSegmentStartX = 0
		this.finalBossPreviousX = 0
		this.finalBossDebugLastLogAtMs = -Infinity
		if (emitHealthHidden)
		{
			this.emitFinalBossHealthChanged(false)
		}
	}

	private logFinalBossDebug(stage: string)
	{
		if (!FINAL_BOSS_DEBUG_LOG_ENABLED || !this.finalBoss)
		{
			return
		}

		const now = this.time.now
		if (stage === 'tick' && (now - this.finalBossDebugLastLogAtMs) < FINAL_BOSS_DEBUG_LOG_INTERVAL_MS)
		{
			return
		}
		this.finalBossDebugLastLogAtMs = now

		const worldView = this.cameras.main.worldView
		// console.info('[final-boss-debug]', {
		// 	stage,
		// 	now,
		// 	map: {
		// 		widthPx: this.currentMapWidthPx,
		// 		maxX: this.currentMapWidthPx
		// 	},
		// 	boss: {
		// 		x: this.finalBoss.x,
		// 		y: this.finalBoss.y,
		// 		visible: this.finalBoss.visible,
		// 		active: this.finalBoss.active,
		// 		alpha: this.finalBoss.alpha
		// 	},
		// 	entry: {
		// 		startX: this.finalBossEntryStartX,
		// 		targetX: this.finalBossEntryTargetX,
		// 		entering: this.finalBossEntering,
		// 		tweenRunning: Boolean(this.finalBossEntryTween && this.finalBossEntryTween.isPlaying())
		// 	},
		// 	patrol: {
		// 		minX: this.finalBossPatrolMinX,
		// 		maxX: this.finalBossPatrolMaxX,
		// 		direction: this.finalBossDirection
		// 	},
		// 	camera: {
		// 		scrollX: this.cameras.main.scrollX,
		// 		worldLeft: worldView.left,
		// 		worldRight: worldView.right,
		// 		locked: this.levelThreeFinalCameraLocked
		// 	},
		// 	playerX: this.penquin?.x
		// })
	}

	private hasLevelThreeReachedCameraEnd(): boolean
	{
		if (this.currentMapWidthPx <= 0)
		{
			return false
		}

		const cameraRightEdgeX = this.cameras.main.worldView.right
		return cameraRightEdgeX >= (this.currentMapWidthPx - LEVEL_THREE_CAMERA_END_TOLERANCE_PX)
	}

	private lockLevelThreeFinalCamera()
	{
		if (this.levelThreeFinalCameraLocked)
		{
			return
		}

		const camera = this.cameras.main
		const maxScrollY = Math.max(0, this.currentMapHeightPx - camera.height)
		const currentScrollY = Phaser.Math.Clamp(camera.scrollY, 0, maxScrollY)
		const groundLockedScrollY = Phaser.Math.Clamp(maxScrollY, 0, maxScrollY)

		this.levelThreeFinalCameraLocked = true
		this.levelThreeFinalCameraScrollX = camera.scrollX
		this.levelThreeFinalCameraScrollY = currentScrollY
		camera.stopFollow()
		camera.setScroll(this.levelThreeFinalCameraScrollX, this.levelThreeFinalCameraScrollY)

		if (this.levelThreeFinalCameraLockTween)
		{
			this.levelThreeFinalCameraLockTween.stop()
			this.levelThreeFinalCameraLockTween = undefined
		}

		if (Math.abs(groundLockedScrollY - currentScrollY) < 0.5)
		{
			this.levelThreeFinalCameraScrollY = groundLockedScrollY
			camera.setScroll(this.levelThreeFinalCameraScrollX, this.levelThreeFinalCameraScrollY)
			return
		}

		this.levelThreeFinalCameraLockTween = this.tweens.addCounter({
			from: currentScrollY,
			to: groundLockedScrollY,
			duration: LEVEL_THREE_FINAL_CAMERA_LOCK_TO_GROUND_TWEEN_MS,
			ease: 'Sine.easeOut',
			onUpdate: tween => {
				this.levelThreeFinalCameraScrollY = tween.getValue()
				camera.setScroll(this.levelThreeFinalCameraScrollX, this.levelThreeFinalCameraScrollY)
			},
			onComplete: () => {
				this.levelThreeFinalCameraScrollY = groundLockedScrollY
				camera.setScroll(this.levelThreeFinalCameraScrollX, this.levelThreeFinalCameraScrollY)
				this.levelThreeFinalCameraLockTween = undefined
			}
		})
	}

	private enforceLevelThreeFinalCameraLock()
	{
		if (!this.levelThreeFinalCameraLocked)
		{
			return
		}

		this.cameras.main.setScroll(this.levelThreeFinalCameraScrollX, this.levelThreeFinalCameraScrollY)
	}

	private enforceLevelThreeVisibleBoundsLock()
	{
		if (!this.levelThreeFinalLockActive || !this.levelThreeFinalCameraLocked || !this.penquin)
		{
			return
		}

		const body = this.penquin.body as MatterJS.BodyType | undefined
		if (!body)
		{
			return
		}

		const leftExtent = Math.max(0, body.position.x - body.bounds.min.x)
		const rightExtent = Math.max(0, body.bounds.max.x - body.position.x)
		const minVisibleX = this.cameras.main.worldView.left + leftExtent
		const maxVisibleX = this.cameras.main.worldView.right - rightExtent
		const currentX = this.penquin.x
		const clampedX = Phaser.Math.Clamp(currentX, minVisibleX, maxVisibleX)

		if (clampedX === currentX)
		{
			return
		}

		this.penquin.setX(clampedX)
		if ((clampedX <= minVisibleX && body.velocity.x < 0) || (clampedX >= maxVisibleX && body.velocity.x > 0))
		{
			this.penquin.setVelocityX(0)
		}

		const matterBodyApi = this.resolveMatterBodyApi()
		matterBodyApi.setPosition(body, {
			x: clampedX,
			y: body.position.y
		})
	}

	private resolveMatterBodyApi()
	{
		return (Phaser.Physics.Matter as unknown as {
			Matter: {
				Body: {
					setPosition: (target: MatterJS.BodyType, position: { x: number, y: number }) => void
				}
			}
		}).Matter.Body
	}

	private findNearestStaticSurfaceYAtX(sampleX: number, minY: number, maxY: number): number | undefined
	{
		const worldBodies = (this.matter.world.engine.world as unknown as { bodies?: MatterJS.BodyType[] }).bodies
		if (!worldBodies || worldBodies.length === 0)
		{
			return undefined
		}

		let nearestSurfaceY = Number.POSITIVE_INFINITY
		for (const candidate of worldBodies)
		{
			if (!candidate.isStatic || candidate.isSensor)
			{
				continue
			}

			if (sampleX < candidate.bounds.min.x || sampleX > candidate.bounds.max.x)
			{
				continue
			}

			const surfaceY = candidate.bounds.min.y
			if (surfaceY < minY || surfaceY > maxY)
			{
				continue
			}

			if (surfaceY < nearestSurfaceY)
			{
				nearestSurfaceY = surfaceY
			}
		}

		return Number.isFinite(nearestSurfaceY) ? nearestSurfaceY : undefined
	}

	private resolveBodyCenterYForGroundSurface(body: MatterJS.BodyType, groundSurfaceY: number): number
	{
		const bodyHeight = Math.max(1, body.bounds.max.y - body.bounds.min.y)
		return groundSurfaceY - (bodyHeight * 0.5)
	}

	private resolveExitGroundY(body: MatterJS.BodyType): number | undefined
	{
		const scannedGroundY = this.findExitGroundYFromStaticBodies(body)
		if (typeof scannedGroundY === 'number' && Number.isFinite(scannedGroundY))
		{
			return scannedGroundY
		}

		if (Math.abs(body.velocity.y) <= LEVEL_END_STABLE_VERTICAL_SPEED_MAX)
		{
			if (typeof this.lastStableGroundY === 'number' && Number.isFinite(this.lastStableGroundY))
			{
				return this.lastStableGroundY
			}

			return body.position.y
		}

		return undefined
	}

	private findExitGroundYFromStaticBodies(body: MatterJS.BodyType): number | undefined
	{
		const worldBodies = (this.matter.world.engine.world as unknown as { bodies?: MatterJS.BodyType[] }).bodies
		if (!worldBodies || worldBodies.length === 0)
		{
			return undefined
		}

		const startX = body.position.x
		const endX = startX + Math.min(LEVEL_END_EXIT_TRAVEL_PX, LEVEL_END_GROUND_SCAN_MAX_LOOKAHEAD_PX)
		const playerBottomOffset = body.position.y - body.bounds.max.y
		let nearestSurfaceY = Number.POSITIVE_INFINITY

		for (let sampleX = startX; sampleX <= endX; sampleX += LEVEL_END_GROUND_SCAN_STEP_PX)
		{
			for (const candidate of worldBodies)
			{
				if (candidate === body || candidate.isSensor || !candidate.isStatic)
				{
					continue
				}

				if (sampleX < candidate.bounds.min.x || sampleX > candidate.bounds.max.x)
				{
					continue
				}

				const surfaceY = candidate.bounds.min.y
				if (surfaceY < body.bounds.min.y)
				{
					continue
				}

				if (surfaceY < nearestSurfaceY)
				{
					nearestSurfaceY = surfaceY
				}
			}
		}

		if (!Number.isFinite(nearestSurfaceY))
		{
			return undefined
		}

		return nearestSurfaceY + playerBottomOffset
	}

	private startLevelExitWalkTween(body: MatterJS.BodyType)
	{
		void body

		if (this.pendingLevelAfterVideoIndex === undefined)
		{
			this.startDailyRankingScene()
			return
		}

		this.playTransitionVideoScene({
			nextLevelIndex: this.pendingLevelAfterVideoIndex
		})
	}

	private startLevelExitSequence()
	{
		if (!this.penquin)
		{
			return
		}

		const nextLevelIndex = this.currentLevelIndex + 1

		this.levelExitSequenceActive = true
		this.disableLevelCountdown(true)
		this.pendingLevelStartBudgetState = this.playerController?.getBudgetState()
		this.pendingLevelAfterVideoIndex = nextLevelIndex < LEVELS.length ? nextLevelIndex : undefined
		if (this.pendingLevelStartBudgetState)
		{
			setPersistentBudgetState(this.pendingLevelStartBudgetState)
		}
		setPersistentPlayTimeState({ elapsedMs: this.elapsedMapTimeMs })
		this.disableInactivityWatchdog()
		this.unbindInactivityInput()
		const body = this.penquin.body as MatterJS.BodyType | undefined
		if (!body)
		{
			return
		}

		this.penquin.setSensor(true)
		this.penquin.setIgnoreGravity(true)
		this.penquin.setVelocity(0, 0)
		this.penquin.flipX = false
		this.penquin.play('player-idle', true)

		this.cameras.main.stopFollow()
		this.startLevelExitWalkTween(body)
	}

	private startDailyRankingScene()
	{
		this.scene.start(DAILY_RANKING_SCENE_KEY, this.buildDailyRankingSceneData())
	}

	private buildDailyRankingSceneData()
	{
		const budgetState = this.pendingLevelStartBudgetState ?? this.playerController?.getBudgetState()
		const nicknameFromRegistry = this.game.registry.get('playerNickname')
		const nickname = typeof nicknameFromRegistry === 'string' && nicknameFromRegistry.trim().length > 0
			? nicknameFromRegistry.trim().toUpperCase()
			: DEFAULT_NICKNAME

		return {
			summary: {
				nickname,
				finalBudget: Math.max(0, Math.round(budgetState?.current ?? 0)),
				finalMaxBudget: Math.max(0, Math.round(budgetState?.max ?? 3000)),
				elapsedMs: Math.max(0, Math.floor(this.elapsedMapTimeMs))
			},
			endReason: 'finished_level'
		}
	}

	private playTransitionVideoScene(options: {
		nextLevelIndex?: number
		targetSceneKey?: string
		targetSceneData?: object
	} = {})
	{
		const videoPath = typeof this.currentLevel.videoPath === 'string' && this.currentLevel.videoPath.trim().length > 0
			? this.currentLevel.videoPath.trim()
			: undefined

		if (!videoPath)
		{
			if (typeof options.nextLevelIndex === 'number')
			{
				this.scene.start('game', {
					levelIndex: options.nextLevelIndex,
					budgetState: this.pendingLevelStartBudgetState
				})
				return
			}

			if (typeof options.targetSceneKey === 'string' && options.targetSceneKey.trim().length > 0)
			{
				this.scene.start(options.targetSceneKey, options.targetSceneData)
			}

			return
		}

		setLogoOverlayVisible(true)
		if (this.levelMusic)
		{
			this.levelMusic.stop()
			this.levelMusic.destroy()
			this.levelMusic = undefined
		}
		this.sound.stopAll()
		this.scene.pause()
		this.scene.launch(LEVEL_TRANSITION_VIDEO_SCENE_KEY, {
			nextLevelIndex: options.nextLevelIndex,
			videoPath,
			budgetState: this.pendingLevelStartBudgetState,
			targetSceneKey: options.targetSceneKey,
			targetSceneData: options.targetSceneData,
			sourceSceneKey: this.scene.key
		})
		this.scene.bringToTop(LEVEL_TRANSITION_VIDEO_SCENE_KEY)
	}

	private hasContinuousActivityInput(): boolean
	{
		return this.cursors.left.isDown
			|| this.cursors.right.isDown
			|| this.cursors.up.isDown
			|| this.cursors.down.isDown
			|| this.input.activePointer.isDown
	}

	private createInactivityModal()
	{
		this.inactivityModal = createSharedInactivityModal(this, {
			depth: INACTIVITY_MODAL_DEPTH
		})
	}

	private destroyInactivityModal()
	{
		this.inactivityModal?.destroy()
		this.inactivityModal = undefined
	}

	private bindInactivityInput()
	{
		this.input.keyboard?.on('keydown', this.trackActivity, this)
		this.input.on('pointerdown', this.trackActivity, this)
		this.scale.on(Phaser.Scale.Events.RESIZE, this.onInactivityModalResize, this)
	}

	private unbindInactivityInput()
	{
		this.input.keyboard?.off('keydown', this.trackActivity, this)
		this.input.off('pointerdown', this.trackActivity, this)
		this.scale.off(Phaser.Scale.Events.RESIZE, this.onInactivityModalResize, this)
	}

	private onInactivityModalResize()
	{
		this.inactivityModal?.reposition()
	}

	private enableInactivityWatchdog()
	{
		this.inactivityEnabled = true
		this.resetInactivityTimers()
	}

	private disableInactivityWatchdog()
	{
		this.inactivityEnabled = false
		this.hideInactivityModal()
		this.clearInactivityTimers()
	}

	private trackActivity()
	{
		if (!this.inactivityEnabled)
		{
			return
		}

		const now = this.time.now
		if (now - this.inactivityLastActivityAtMs < 120)
		{
			return
		}

		this.inactivityLastActivityAtMs = now
		this.hideInactivityModal()
		this.resetInactivityTimers()
	}

	private resetInactivityTimers()
	{
		if (!this.inactivityEnabled)
		{
			return
		}

		this.clearInactivityTimers()
		this.inactivityWarningEvent = this.time.delayedCall(INACTIVITY_WARNING_DELAY_MS, () => {
			this.showInactivityModal()

			this.inactivityRestartEvent = this.time.delayedCall(INACTIVITY_RESTART_DELAY_MS, () => {
				this.restartToIntroFirstScreen()
			})

			this.startInactivityCountdown()
		})
	}

	private clearInactivityTimers()
	{
		if (this.inactivityWarningEvent)
		{
			this.inactivityWarningEvent.remove(false)
			this.inactivityWarningEvent = undefined
		}

		if (this.inactivityRestartEvent)
		{
			this.inactivityRestartEvent.remove(false)
			this.inactivityRestartEvent = undefined
		}

		if (this.inactivityCountdownEvent)
		{
			this.inactivityCountdownEvent.remove(false)
			this.inactivityCountdownEvent = undefined
		}
	}

	private startInactivityCountdown()
	{
		if (this.inactivityCountdownEvent)
		{
			this.inactivityCountdownEvent.remove(false)
			this.inactivityCountdownEvent = undefined
		}

		this.updateInactivityCountdown()

		this.inactivityCountdownEvent = this.time.addEvent({
			delay: 200,
			loop: true,
			callback: () => {
				this.updateInactivityCountdown()
			}
		})
	}

	private updateInactivityCountdown()
	{
		if (!this.inactivityRestartEvent)
		{
			this.inactivityModal?.setCountdownSeconds(null)
			return
		}

		const remainingSeconds = Math.max(0, Math.ceil(this.inactivityRestartEvent.getRemaining() / 1000))
		this.inactivityModal?.setCountdownSeconds(remainingSeconds)
	}

	private showInactivityModal()
	{
		this.inactivityModal?.show()
	}

	private hideInactivityModal()
	{
		this.inactivityModal?.setCountdownSeconds(null)
		this.inactivityModal?.hide()
	}

	private restartToIntroFirstScreen()
	{
		this.disableInactivityWatchdog()
		void this.finishRunByInactivityTimeout()
		this.scene.stop('ui')
		this.scene.stop('logo-overlay')
		this.scene.start('intro')
	}

	private async finishRunByInactivityTimeout()
	{
		if (!this.rankingApi)
		{
			return
		}

		const session = getPersistentRankingRunSession()
		if (!session || session.finished)
		{
			return
		}

		const budgetState = this.playerController?.getBudgetState()
		const finalBudget = 0
		const finalMaxBudget = Math.max(0, Math.round(budgetState?.max ?? 3000))
		const nicknameFromRegistry = this.game.registry.get('playerNickname')
		const nickname = typeof nicknameFromRegistry === 'string' && nicknameFromRegistry.trim().length > 0
			? nicknameFromRegistry.trim().toUpperCase()
			: DEFAULT_NICKNAME
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
		const finishPayload = {
			seq_final: Math.max(1, session.seq),
			end_reason: 'timeout' as const,
			client_ended_at_ms: Date.now(),
			final_budget: finalBudget,
			final_max_budget: finalMaxBudget,
			tz: timezone,
			summary: {
				nickname,
				elapsed_ms: Math.max(0, Math.floor(this.elapsedMapTimeMs)),
				inactivity_timeout: true
			}
		}

		if (isLocalSessionId(session.runId))
		{
			enqueueFinishOperation(session.runId, finishPayload)
			setPersistentRankingRunSession({
				...session,
				finished: true
			})
			return
		}

		try
		{
			await this.rankingApi.finishRun({ ...finishPayload, run_id: session.runId }, session.runToken)

			setPersistentRankingRunSession({
				...session,
				finished: true
			})
		}
		catch (error)
		{
			// console.warn('[ranking-api] finish failed on inactivity timeout, queuing for later sync', error)
			enqueueFinishOperation(session.runId, finishPayload, session.runToken)
			setPersistentRankingRunSession({
				...session,
				finished: true
			})
		}
	}
}
